import { Box, Dialog, LinearProgress, Tooltip, Popover } from '@mui/material'
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import EngineeringIcon from '@mui/icons-material/Engineering'
import GroupsIcon from '@mui/icons-material/Groups'
import InventoryIcon from '@mui/icons-material/Inventory2'
import SyncIcon from '@mui/icons-material/Sync'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import Papa from 'papaparse'
import toast from 'react-hot-toast'
import {
  FONT_MONO,
  FONT_SANS,
  TOKENS,
  TYPE,
  SchChip,
  PanelHead,
  PillButton,
  RADII,
} from './SchedulerDesign'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
// Shared with the Changeover grid's popper so both surfaces describe an event
// the same way — the engine's `reason` wire format is parsed in one place.
import {
  changeoverWhat,
  parseChangeoverReason,
} from 'Features/SchedulerView/ChangeoverReason'
import {
  clipMarksOf,
  rebaseRowsToWeek,
} from 'Features/SchedulerView/ClipToWeek'
import {
  ScheduleVariantContext,
  useUpdateScheduleVersionTableData,
} from 'Features/SchedulerView/SchedulerView.data'
import {
  CleanRef,
  cleanReason,
  cleanRemovalRisk,
  resolveCleanRef,
  stripCleanFromRows,
} from 'Features/SchedulerView/RemoveClean'
import {
  adjustOpacity,
  getKitchenColor,
  getKitchenColorCache,
} from 'Features/SchedulerView/RenderCell.view'
import {
  ProductionRequirementRowType,
  SchedulerCommentAnchor,
  SchedulerPackagingRoutesRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
  SynchronizedLinesRowType,
  WorkstationDownTimeRowType,
} from './Scheduler.types'
import {
  SchedulerComment,
  SchedulerProgress,
  SchedulerRunConfig,
  usePublishedSchedules,
} from './Scheduler.data'
import {
  CommentTarget,
  commentsFor,
  downtimeAnchor,
  useCommentIndex,
  useCurrentUserEmail,
} from './ScheduleComments.anchor'
import {
  CommentDot,
  COMMENT_ACCENT_DOWNTIME,
  CommentHoverBlock,
  ScheduleCommentPopover,
} from './ScheduleCommentPopover.view'
import { ScheduleResourceUsageView } from './ScheduleResourceUsage.view'
import { MidWeekRescheduleTab } from './MidWeekRescheduleDialog.view'
import { MDRunFocus, ModelDescriptionTab } from './ModelDescription.view'
import { TLFilterSelect, TLSearchBox } from './SchedulerFilterSelect'
import {
  FactoryRowOrderControl,
  OrderableRow,
  applyRowOrder,
  useFactoryRowOrder,
} from './FactoryRowOrder'

// ─── Tabs ─────────────────────────────────────────────────────────────────────

// Order here IS the order in the strip, left to right. Resource usage leads
// because it is the view planners open first; the default tab below is derived
// from this list rather than hard-coded, so reordering moves both together.
const TABS = [
  { id: 'resources', label: 'Resource usage' },
  { id: 'crew', label: 'Crew & capacity' },
  { id: 'demand', label: 'Demand fulfilment' },
  { id: 'model', label: 'Model description' },
  { id: 'sync', label: 'Sync groups' },
] as const

/**
 * Output tabs hidden for now.
 *
 * Temporary and reversible: empty the set to bring the tab back. The entry
 * stays in `TABS` so `TabId` still includes it and the render branch below
 * still typechecks — only the strip and the branch are gated, in step, so the
 * strip can never offer a tab that renders nothing and the state can never
 * render a view with no tab to leave by.
 *
 * The matching tab on the output page's own Gantt is hidden separately, in
 * `ScheduleFullGantt.view.tsx` — two surfaces, two switches.
 */
// `crew` (Crew & capacity) was built for another tenant's crew model and
// also counts concurrent lines per hour, where the engine caps distinct lines
// per configured period — hidden until it is rebuilt against that rule.
const HIDDEN_TABS: ReadonlySet<string> = new Set<string>(['sync', 'crew'])

const isTabHidden = (id: string): boolean => HIDDEN_TABS.has(id)

// Kept separate from TABS (and rendered pushed right in the tab strip) —
// it's an action/planning tool rather than an output view like the others.
const RESCHEDULE_TAB = {
  id: 'reschedule',
  label: 'Mid-week reschedule',
} as const
type TabId = (typeof TABS)[number]['id'] | typeof RESCHEDULE_TAB.id

/** The leftmost visible tab — what the strip opens on.
 *
 *  Derived rather than named, so reordering `TABS` or hiding the current first
 *  tab can never leave the default pointing at a tab that is not there. Falls
 *  back to the reschedule tab only in the degenerate case where every output
 *  tab is hidden. */
const DEFAULT_TAB_ID: TabId =
  TABS.find((t) => !isTabHidden(t.id))?.id ?? RESCHEDULE_TAB.id

// ─── Shared constants ─────────────────────────────────────────────────────────

const TOTAL_HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const T = { fontFamily: FONT_SANS }
const MONO_SX = {
  fontFamily: FONT_MONO,
  fontVariantNumeric: 'tabular-nums' as const,
}

// Why every view in this file rebases before rendering: a multi-week
// selection is ONE solve across a `weeks * 168` horizon, and scheduler_api
// writes each week's Delta with ABSOLUTE horizon times — week 2 starts at
// hour 168, not 0 — bucketing a spanning row into every week it touches
// (`azure_endpoint.rs::bucket_rows_into_slices`). Everything here renders
// against the fixed TOTAL_HOURS grid.
//
// Skipping it does not draw the week shifted, it draws it EMPTY: a week-2
// row has `start >= 168`, so `OBand`'s
// `min(TOTAL_HOURS, end) - max(0, start)` goes <= 0 and the bar is dropped
// outright. `rebaseRowsToWeek` lives in `SchedulerView/ClipToWeek.ts` so a
// row lands on the same hour here, in the output tables and in the CSV.

/**
 * Bar flags saying this activity crosses the week boundary — spread into a
 * `TLBarData` alongside the times it was built from.
 *
 * Rebasing clamps a carry-over run to hour 0, which makes it look identical
 * to one that genuinely starts Monday 00:00. `clipRowToWeek` records which
 * edges it truncated for exactly this reason; `truncStart` means the
 * activity began in an earlier week, `truncEnd` that it continues into a
 * later one. `startField` is the same field name the bar's `start` came
 * from, which is how the marks are keyed.
 */
const truncOf = (
  row: InputRow,
  startField: string
): { truncStart?: true; truncEnd?: true } => {
  const m = clipMarksOf(row)[startField]
  return m
    ? {
        ...(m.start && { truncStart: m.start }),
        ...(m.end && { truncEnd: m.end }),
      }
    : {}
}

// ─── Shared output-tab primitives ─────────────────────────────────────────────

const pct = (h: number) => `${(h / TOTAL_HOURS) * 100}%`

const OWeekTrack = memo(
  ({
    height = 22,
    bg = TOKENS.ganttIdle,
    children,
  }: {
    height?: number
    bg?: string
    children?: React.ReactNode
  }) => (
    <Box
      sx={{
        position: 'relative',
        height,
        bgcolor: bg,
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '3px',
        overflow: 'hidden',
      }}
    >
      {[1, 2, 3, 4, 5, 6].map((d) => (
        <Box
          key={d}
          sx={{
            position: 'absolute',
            left: pct(d * 24),
            top: 0,
            bottom: 0,
            width: '1px',
            bgcolor: 'rgba(14,27,34,0.12)',
            zIndex: 1,
          }}
        />
      ))}
      {children}
    </Box>
  )
)
OWeekTrack.displayName = 'OWeekTrack'

const OBand = memo(
  ({
    start,
    end,
    color,
    label,
    hatch,
    title: ttl,
    outline,
    top = 2,
    bottom = 2,
    radius = 2,
  }: {
    start: number
    end: number
    color: string
    label?: string
    hatch?: boolean
    title?: string
    outline?: string
    top?: number
    bottom?: number
    radius?: number
  }) => {
    const width = Math.max(0, Math.min(TOTAL_HOURS, end) - Math.max(0, start))
    if (width <= 0) return null
    return (
      <Tooltip title={ttl ?? ''} arrow disableHoverListener={!ttl}>
        <Box
          sx={{
            position: 'absolute',
            left: pct(Math.max(0, start)),
            width: pct(width),
            top,
            bottom,
            zIndex: 2,
            background: hatch
              ? `repeating-linear-gradient(45deg, ${color} 0 5px, rgba(255,255,255,0.35) 5px 10px)`
              : color,
            border: outline ? `1.5px solid ${outline}` : 'none',
            borderRadius: radius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {label && (
            <Box
              component="span"
              sx={{
                fontSize: 8.5,
                fontWeight: 700,
                color: '#fff',
                fontFamily: FONT_MONO,
                whiteSpace: 'nowrap',
                px: '3px',
              }}
            >
              {label}
            </Box>
          )}
        </Box>
      </Tooltip>
    )
  }
)
OBand.displayName = 'OBand'

const WeekDayHeader = memo(() => (
  <Box sx={{ display: 'flex', mb: '4px' }}>
    {DAY_LABELS.map((d) => (
      <Box
        key={d}
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: TOKENS.ink400,
        }}
      >
        {d}
      </Box>
    ))}
  </Box>
))
WeekDayHeader.displayName = 'WeekDayHeader'

// ─── PanelHead ────────────────────────────────────────────────────────────────
// Moved to SchedulerDesign.tsx — shared with MidWeekRescheduleDialog.view.tsx
// (which is itself imported by this file, so PanelHead can't live here without
// creating a circular import).

// ═══════════════════════════════════════════════════════════════════════════════
// TIMELINE TAB  (ports ScheduleTimeline + TimelineRows + TimelineBars)
// ═══════════════════════════════════════════════════════════════════════════════

// Sticky left rail width. The resource name is what a planner reads the
// chart by, so it gets the room: 232 truncated most product names.
const RAIL = 320

type TLBarData = {
  start: number
  end: number
  type:
    | 'production'
    | 'clean'
    | 'preclean'
    | 'maxrunclean'
    | 'changeover'
    | 'downtime'
  stage?: 'base' | 'pack'
  product?: string
  productDescription?: string
  qty?: number
  rate?: number | null
  /** Slide-9 detail, set on LINE production bars only — see `lineDetail`.
   *  Deliberately absent from kitchen and tank bars: those are deduped across
   *  every row sharing a cook window, so `row.line` there is whichever row
   *  happened to build the bar rather than a fact about it. */
  lineName?: string
  kitchenName?: string
  /** Base material the run is packed from — the deck's "Input Product". */
  inputProduct?: string
  /** `pr` off the output row, verbatim. A string because the engine writes it
   *  as one and it already carries its own unit in live data. */
  prFactor?: string
  /** `runRateBase` — the kitchen's rate for this base material. */
  kitchenRate?: number | null
  color?: string
  edgeColor?: string
  /** Activity began before this week — its bar is clamped to hour 0. */
  truncStart?: true
  /** Activity continues past this week — its bar is cut off at hour 168. */
  truncEnd?: true
  /** Present on clean bars only: the identity needed to remove this clean
   *  from the schedule. Set wherever a clean bar is built, so clicking one is
   *  actionable from any grouping rather than only the Cleaning tab. */
  clean?: CleanRef
  /** Present on production bars only: the identity a comment on this batch is
   *  stored against. Set wherever a production bar is built, for the same
   *  reason `clean` is — the row's own name is the kitchen in one grouping,
   *  the line in another and the product description in a third, so the
   *  anchor has to travel with the bar to stay the same in all of them. */
  anchor?: SchedulerCommentAnchor
  /** Present on changeover bars: the engine's `reason` for the event, verbatim
   *  off the wire. Carried so the tooltip can say what the change is FOR
   *  rather than just that one happened — a bottle-format change and an
   *  equipment switch draw the same bar otherwise. Absent on the scalar
   *  primary event, which has no reason field of its own. */
  changeoverReason?: string
  /** Mid-week reschedule provenance, on production bars only. Exactly one of
   *  these is set, and ONLY on a result that is actually a reschedule — an
   *  ordinary run sets neither, so its bars draw exactly as they always did.
   *
   *  `carriedOver` is history the reschedule replayed (it ran before the
   *  cut-off); `replanned` is what the optimizer produced this time. The
   *  distinction is the whole point of looking at a reschedule, and without
   *  it the two are indistinguishable on the timeline. */
  carriedOver?: true
  replanned?: true
}

/** The extra detail the hover widget shows on a packing bar (client deck
 *  2026-09-16, slide 9: Line, PR factor, Kitchen, Kitchen rate, Input
 *  product).
 *
 *  Module scope because two separate components build packing bars — the
 *  kitchen/line grouping pass and the per-resource pass — and they must not
 *  drift in what a bar carries.
 *
 *  Applied at the LINE production sites only. A kitchen bar is keyed by
 *  (start, end, base_material) and shared by every line that cook feeds, so
 *  stamping it with one row's line would print an arbitrary one of them. */
const lineDetail = (
  row: InputRow
): Pick<
  TLBarData,
  'lineName' | 'kitchenName' | 'inputProduct' | 'prFactor' | 'kitchenRate'
> => ({
  lineName: row.line || undefined,
  kitchenName: row.kitchen || undefined,
  inputProduct: row.base_material_description || row.base_material || undefined,
  prFactor: row.pr || undefined,
  kitchenRate: row.runRateBase ?? undefined,
})

type TLRowData = {
  name: string
  /** Stable identity for the saved custom row order, where `name` alone is
   *  not unique (a Combined product row is product + line + kitchen). */
  id?: string
  bars: TLBarData[]
  description?: string
  util: number
  prodH: number
  cleanH: number
  coCount: number
  qty: number
  baseBatches?: number
}

type TLGroup = { kitchen: TLRowData; lines: TLRowData[]; rollUtil: number }
type TLHoverTip = { b: TLBarData; x: number; y: number } | null

function utilBand(u: number): { fg: string; track: string } {
  if (u >= 85)
    return { fg: TOKENS.stateRunning, track: 'rgba(199,127,42,0.18)' }
  if (u >= 60)
    return { fg: TOKENS.stateComplete, track: 'rgba(14,124,134,0.16)' }
  if (u >= 35) return { fg: TOKENS.blue600, track: 'rgba(0,130,179,0.16)' }
  return { fg: TOKENS.ink400, track: 'rgba(113,128,138,0.16)' }
}

// Wire-format emitted by changeover_events_to_json in postprocess.rs
type ChangeoverEvent = {
  start: number
  end: number
  changeover_asset?: string
  asset_id?: string
  reason?: string
}
const parseChangeoverEvents = (
  raw: string | null | undefined
): ChangeoverEvent[] => {
  if (!raw) return []
  try {
    const p = JSON.parse(raw)
    return Array.isArray(p) ? p : []
  } catch {
    return []
  }
}

// Wire-format for additional_kitchen_cips / additional_line_cips, e.g.
// {"cip":"CIP1","cip_id":"650","start":72,"end":77,"reason":"max_run_hours"}.
// `reason` is one of the decoder's CIP triggers (simplified_decoder.rs):
// "idle_preclean" | "material_change" | "max_run_hours" | "end_of_use".
type AdditionalCipEvent = {
  start: number
  end: number
  cip?: string
  cip_id?: string
  reason?: string
}
const parseAdditionalCips = (
  raw: string | null | undefined
): AdditionalCipEvent[] => {
  if (!raw) return []
  try {
    const p = JSON.parse(raw)
    return Array.isArray(p) ? p : []
  } catch {
    return []
  }
}

// The scalar kitchen_preclean_*/line_preclean_* fields are a "promoted" copy
// of exactly one entry from additional_*_cips (matched by start/end) — this
// looks up that entry's `reason` so callers can tell a genuine idle-triggered
// preclean apart from a mid-run `max_run_hours` clean (same field, different
// cause: the kitchen/line ran continuously too long, not that it sat idle or
// is switching product).
const cipReasonFor = (
  raw: string | null | undefined,
  start: number | null | undefined,
  end: number | null | undefined
): string | undefined => {
  if (start == null || end == null) return undefined
  return parseAdditionalCips(raw).find(
    (e) => e.start === start && e.end === end
  )?.reason
}

// Reuses the same per-kitchen colours as the SchedulerView grid (pinned
// client colours from kitchenColors.config.ts, falling back to a rotating
// palette for anything unlisted) so a kitchen looks the same across views.
const parseRgbaTuple = (rgba: string): [number, number, number] => {
  const match = rgba.match(/[\d.]+/g)
  const [r, g, b] = match ? match.map(Number) : [0, 0, 0]
  return [r, g, b]
}
// 40% opacity — same softening RenderCell.view.tsx's constructStyle applies
// for the table view (the raw palette in kitchenColors.config.ts is full
// primary saturation and is far too glaring rendered solid on a Gantt bar).
const kitchenFill = (k: string) => {
  const [r, g, b] = parseRgbaTuple(getKitchenColor(k))
  return `rgba(${r}, ${g}, ${b}, 0.4)`
}
const kitchenEdge = (k: string) => {
  const [r, g, b] = parseRgbaTuple(getKitchenColor(k))
  const factor = 0.6
  return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`
}

const parseDowntimeStr = (s: string): [number, number][] => {
  if (!s || s === 'null' || s === 'None') return []
  const intervals: [number, number][] = []
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null)
    intervals.push([Math.round(parseFloat(m[1])), Math.round(parseFloat(m[2]))])
  return intervals
}

function buildTimelineData(
  outputRows: InputRow[],
  downtimeData: WorkstationDownTimeRowType[] = []
) {
  // Mid-week reschedule provenance for a production bar. Only a reschedule
  // carries replayed history, so the presence of ANY fixed row is what makes
  // the distinction meaningful — on an ordinary run this returns nothing and
  // every bar draws exactly as before.
  const isReschedule = outputRows.some((r) => r.fixed === true)
  const provenance = (
    row: InputRow
  ): Pick<TLBarData, 'carriedOver' | 'replanned'> =>
    !isReschedule
      ? {}
      : row.fixed === true
        ? { carriedOver: true }
        : { replanned: true }

  const kitchenBarsMap = new Map<string, Map<string, TLBarData>>()
  const kitchenCleanMap = new Map<string, Map<string, TLBarData>>()
  const kitchenPrecleanMap = new Map<string, Map<string, TLBarData>>()
  const kitchenChangeoverMap = new Map<string, Map<string, TLBarData>>()
  const lineProdBars = new Map<string, TLBarData[]>()
  const lineCleanMap = new Map<string, Map<string, TLBarData>>()
  const linePrecleanMap = new Map<string, Map<string, TLBarData>>()
  const lineChangeoverMap = new Map<string, Map<string, TLBarData>>()
  // Same production/clean/changeover bars as the plain line-keyed maps above,
  // but keyed by `${kitchen}||${line}` so the "By kitchen" view can show only
  // the bars a given kitchen actually caused on a shared line — a line fed by
  // multiple kitchens must not show every kitchen's activity duplicated
  // under each kitchen's section.
  const kitchenLineProdBars = new Map<string, TLBarData[]>()
  const kitchenLineCleanMap = new Map<string, Map<string, TLBarData>>()
  const kitchenLinePrecleanMap = new Map<string, Map<string, TLBarData>>()
  const kitchenLineChangeoverMap = new Map<string, Map<string, TLBarData>>()
  const kitchenToLines = new Map<string, Set<string>>()

  const addChangeover = (
    map: Map<string, Map<string, TLBarData>>,
    key: string,
    s: number,
    e: number,
    reason?: string
  ) => {
    if (s == null || e == null || e <= s) return
    const ck = `${s}|${e}`
    if (!map.has(key)) map.set(key, new Map())
    const bucket = map.get(key)!
    const existing = bucket.get(ck)
    if (!existing) {
      bucket.set(ck, {
        start: s,
        end: e,
        type: 'changeover',
        changeoverReason: reason,
      })
      return
    }
    // Same window already added. The scalar primary event and the JSON array
    // usually describe the SAME changeover, and only the JSON half carries a
    // reason — so let a reason fill in over a blank rather than letting
    // whichever was added first win and lose the description.
    if (!existing.changeoverReason && reason) existing.changeoverReason = reason
  }

  for (const row of outputRows) {
    const k = row.kitchen
    const l = row.line
    if (k) {
      if (!kitchenBarsMap.has(k)) kitchenBarsMap.set(k, new Map())
      if (!kitchenCleanMap.has(k)) kitchenCleanMap.set(k, new Map())
      if (!kitchenPrecleanMap.has(k)) kitchenPrecleanMap.set(k, new Map())
      if (row.kitchen_start_time != null && row.kitchen_end_time != null) {
        const key = `${row.kitchen_start_time}|${row.kitchen_end_time}|${row.base_material ?? ''}`
        if (!kitchenBarsMap.get(k)!.has(key))
          kitchenBarsMap.get(k)!.set(key, {
            start: row.kitchen_start_time,
            end: row.kitchen_end_time,
            ...truncOf(row, 'kitchen_start_time'),
            type: 'production',
            stage: 'base',
            product: row.base_material ?? '',
            productDescription: row.base_material_description ?? '',
            color: kitchenFill(k),
            edgeColor: kitchenEdge(k),
            ...provenance(row),
            anchor: {
              resourceName: k,
              resourceType: 'kitchen',
              productCode: row.base_material ?? '',
            },
          })
      }
      if (
        row.kitchen_clean_start_time != null &&
        row.kitchen_clean_end_time != null
      ) {
        const key = `${row.kitchen_clean_start_time}|${row.kitchen_clean_end_time}`
        if (!kitchenCleanMap.get(k)!.has(key))
          kitchenCleanMap.get(k)!.set(key, {
            start: row.kitchen_clean_start_time,
            end: row.kitchen_clean_end_time,
            ...truncOf(row, 'kitchen_clean_start_time'),
            type: 'clean',
            clean: {
              side: 'kitchen',
              resource: k,
              kind: 'clean',
              start: row.kitchen_clean_start_time,
              end: row.kitchen_clean_end_time,
            },
          })
      }
      if (
        row.kitchen_preclean_start_time != null &&
        row.kitchen_preclean_end_time != null
      ) {
        const key = `${row.kitchen_preclean_start_time}|${row.kitchen_preclean_end_time}`
        if (!kitchenPrecleanMap.get(k)!.has(key))
          kitchenPrecleanMap.get(k)!.set(key, {
            start: row.kitchen_preclean_start_time,
            end: row.kitchen_preclean_end_time,
            ...truncOf(row, 'kitchen_preclean_start_time'),
            clean: {
              side: 'kitchen',
              resource: k,
              kind: 'preclean',
              start: row.kitchen_preclean_start_time,
              end: row.kitchen_preclean_end_time,
            },
            type:
              cipReasonFor(
                row.additional_kitchen_cips,
                row.kitchen_preclean_start_time,
                row.kitchen_preclean_end_time
              ) === 'max_run_hours'
                ? 'maxrunclean'
                : 'preclean',
          })
      }
      // Kitchen changeover — scalar primary event
      if (
        row.kitchen_changeover_start_time != null &&
        row.kitchen_changeover_end_time != null
      )
        addChangeover(
          kitchenChangeoverMap,
          k,
          row.kitchen_changeover_start_time,
          row.kitchen_changeover_end_time
        )
      // Kitchen changeover — multi-event JSON array
      for (const evt of parseChangeoverEvents(
        row.additional_kitchen_changeovers
      ))
        addChangeover(kitchenChangeoverMap, k, evt.start, evt.end, evt.reason)
    }
    if (l) {
      if (!lineProdBars.has(l)) lineProdBars.set(l, [])
      if (!lineCleanMap.has(l)) lineCleanMap.set(l, new Map())
      const klKey = k ? `${k}||${l}` : null

      if (row.line_start_time != null && row.line_end_time != null) {
        const prodBar: TLBarData = {
          start: row.line_start_time,
          end: row.line_end_time,
          ...truncOf(row, 'line_start_time'),
          type: 'production',
          stage: 'pack',
          product: row.productCode ?? '',
          productDescription: row.product_description ?? '',
          qty: row.quantity ?? 0,
          rate: row.runRateLine ?? null,
          ...lineDetail(row),
          color: k ? kitchenFill(k) : undefined,
          edgeColor: k ? kitchenEdge(k) : undefined,
          ...provenance(row),
          anchor: {
            resourceName: l,
            resourceType: 'line',
            productCode: row.productCode ?? '',
          },
        }
        lineProdBars.get(l)!.push(prodBar)
        if (klKey) {
          if (!kitchenLineProdBars.has(klKey))
            kitchenLineProdBars.set(klKey, [])
          kitchenLineProdBars.get(klKey)!.push(prodBar)
        }
      }
      if (
        row.line_clean_start_time != null &&
        row.line_clean_end_time != null
      ) {
        const key = `${row.line_clean_start_time}|${row.line_clean_end_time}`
        const cleanBar: TLBarData = {
          start: row.line_clean_start_time,
          end: row.line_clean_end_time,
          ...truncOf(row, 'line_clean_start_time'),
          type: 'clean',
          clean: {
            side: 'line',
            resource: l,
            kind: 'clean',
            start: row.line_clean_start_time,
            end: row.line_clean_end_time,
          },
        }
        if (!lineCleanMap.get(l)!.has(key))
          lineCleanMap.get(l)!.set(key, cleanBar)
        if (klKey) {
          if (!kitchenLineCleanMap.has(klKey))
            kitchenLineCleanMap.set(klKey, new Map())
          if (!kitchenLineCleanMap.get(klKey)!.has(key))
            kitchenLineCleanMap.get(klKey)!.set(key, cleanBar)
        }
      }
      if (
        row.line_preclean_start_time != null &&
        row.line_preclean_end_time != null
      ) {
        const key = `${row.line_preclean_start_time}|${row.line_preclean_end_time}`
        const precleanBar: TLBarData = {
          start: row.line_preclean_start_time,
          end: row.line_preclean_end_time,
          ...truncOf(row, 'line_preclean_start_time'),
          clean: {
            side: 'line',
            resource: l,
            kind: 'preclean',
            start: row.line_preclean_start_time,
            end: row.line_preclean_end_time,
          },
          type:
            cipReasonFor(
              row.additional_line_cips,
              row.line_preclean_start_time,
              row.line_preclean_end_time
            ) === 'max_run_hours'
              ? 'maxrunclean'
              : 'preclean',
        }
        if (!linePrecleanMap.has(l)) linePrecleanMap.set(l, new Map())
        if (!linePrecleanMap.get(l)!.has(key))
          linePrecleanMap.get(l)!.set(key, precleanBar)
        if (klKey) {
          if (!kitchenLinePrecleanMap.has(klKey))
            kitchenLinePrecleanMap.set(klKey, new Map())
          if (!kitchenLinePrecleanMap.get(klKey)!.has(key))
            kitchenLinePrecleanMap.get(klKey)!.set(key, precleanBar)
        }
      }
      // Line changeover — scalar primary event
      if (
        row.line_changeover_start_time != null &&
        row.line_changeover_end_time != null
      ) {
        addChangeover(
          lineChangeoverMap,
          l,
          row.line_changeover_start_time,
          row.line_changeover_end_time
        )
        if (klKey)
          addChangeover(
            kitchenLineChangeoverMap,
            klKey,
            row.line_changeover_start_time,
            row.line_changeover_end_time
          )
      }
      // Line changeover — multi-event JSON array
      for (const evt of parseChangeoverEvents(
        row.additional_line_changeovers
      )) {
        addChangeover(lineChangeoverMap, l, evt.start, evt.end, evt.reason)
        if (klKey)
          addChangeover(
            kitchenLineChangeoverMap,
            klKey,
            evt.start,
            evt.end,
            evt.reason
          )
      }
      if (k) {
        if (!kitchenToLines.has(k)) kitchenToLines.set(k, new Set())
        kitchenToLines.get(k)!.add(l)
      }
    }
  }

  // ── Holding tank: the drain that occupies neither slot ──
  //
  // A fill (`KTN1 -> TANK01`) and a drain to a line (`TANK01 -> LINE1`) both
  // put the vessel in one of the two asset slots, so the loop above has
  // already drawn them. A drain into a second kitchen
  // (`TANK02 -> PKN12 -> CTL01`) does not: both slots hold real assets, and the
  // vessel supplies that run's input as a maturity floor and a supply cap
  // rather than as an endpoint. Without this the tank's lane shows its fill and
  // then nothing for the rest of the horizon, and the Kitchen2 run appears to
  // come from nowhere.
  //
  for (const row of outputRows) {
    const t = row.tankId
    if (!t || t === row.kitchen || t === row.line) continue
    if (row.kitchen_start_time == null || row.kitchen_end_time == null) continue
    if (row.kitchen_end_time <= row.kitchen_start_time) continue
    // The KITCHEN window, not the line window: the vessel empties while
    // Kitchen2 draws, which is theta hours before the line it then feeds.
    const bar: TLBarData = {
      start: row.kitchen_start_time,
      end: row.kitchen_end_time,
      ...truncOf(row, 'kitchen_start_time'),
      type: 'production',
      stage: 'base',
      product: row.productCode ?? '',
      productDescription: row.product_description ?? '',
      qty: row.quantity ?? 0,
      rate: row.runRateBase ?? null,
      // Coloured by the kitchen doing the drawing, so the bar reads as "PKN12
      // is pulling from this vessel" rather than as production of its own.
      color: row.kitchen ? kitchenFill(row.kitchen) : undefined,
      edgeColor: row.kitchen ? kitchenEdge(row.kitchen) : undefined,
      ...provenance(row),
      anchor: {
        resourceName: t,
        resourceType: 'line',
        productCode: row.productCode ?? '',
      },
    }
    if (!lineProdBars.has(t)) lineProdBars.set(t, [])
    lineProdBars.get(t)!.push(bar)
    // Nest it under the kitchen DOING THE DRAWING, not the one that filled the
    // vessel. Lanes here are grouped by kitchen, and a group only exists when
    // that kitchen has rows in the week being viewed — so hanging the drain off
    // the filling kitchen renders nothing at all in the week the drain happens.
    // On 31692634253 week 2 holds PKN12 and TANK01 and no PKN08, so a bar keyed
    // `PKN08||TANK02` had no group to live in; in week 1 the same key drew a
    // week-2 activity under the wrong kitchen.
    //
    // The drawing kitchen is always present by construction — it is the kitchen
    // of the very row being drawn — and it is what the bar means: "PKN12 is
    // pulling from this vessel". The vessel therefore appears beneath whichever
    // kitchen it is interacting with, its fill under Kitchen1 and its drain
    // under Kitchen2.
    const k = row.kitchen
    if (k) {
      const klKey = `${k}||${t}`
      if (!kitchenLineProdBars.has(klKey)) kitchenLineProdBars.set(klKey, [])
      kitchenLineProdBars.get(klKey)!.push(bar)
      if (!kitchenToLines.has(k)) kitchenToLines.set(k, new Set())
      kitchenToLines.get(k)!.add(t)
    }
  }

  // Build downtime lookup: resource name → downtime bars
  const downtimeMap = new Map<string, TLBarData[]>()
  for (const dt of downtimeData) {
    if (!dt.resource || !dt.downTime) continue
    const intervals = parseDowntimeStr(dt.downTime)
    if (intervals.length === 0) continue
    downtimeMap.set(
      dt.resource,
      intervals.map(([s, e]) => ({
        start: s,
        end: e,
        type: 'downtime' as const,
        // Comments on downtime are per RESOURCE, per week — the same thread
        // the Workstation Downtime editor shows, not a per-window one. The
        // anchor ignores `resourceType`, which is what lets the free-text
        // value on the downtime row ('Line' / 'line' / 'Constraint') match
        // the Gantt's own notion of the resource.
        anchor: downtimeAnchor(dt.resource, dt.resourceType ?? ''),
      }))
    )
  }

  const makeLineRowFromParts = (
    name: string,
    prod: TLBarData[],
    clean: TLBarData[],
    changeovers: TLBarData[],
    preclean: TLBarData[] = []
  ): TLRowData => {
    const downtime = downtimeMap.get(name) ?? []
    const bars = [
      ...prod,
      ...clean,
      ...preclean,
      ...changeovers,
      ...downtime,
    ].sort((a, b) => a.start - b.start)
    const prodH = prod.reduce((s, b) => s + (b.end - b.start), 0)
    // Count changeovers: actual changeover bars + consecutive different-product transitions
    const coCount =
      changeovers.length ||
      (() => {
        let c = 0
        const sorted = prod.slice().sort((a, b) => a.start - b.start)
        for (let i = 1; i < sorted.length; i++)
          if (sorted[i].product !== sorted[i - 1].product) c++
        return c
      })()
    return {
      name,
      bars,
      util: Math.round((prodH / TOTAL_HOURS) * 100),
      prodH,
      cleanH:
        clean.reduce((s, b) => s + (b.end - b.start), 0) +
        preclean.reduce((s, b) => s + (b.end - b.start), 0),
      coCount,
      qty: prod.reduce((s, b) => s + (b.qty ?? 0), 0),
    }
  }

  const makeLineRow = (name: string): TLRowData =>
    makeLineRowFromParts(
      name,
      lineProdBars.get(name) ?? [],
      Array.from(lineCleanMap.get(name)?.values() ?? []),
      Array.from(lineChangeoverMap.get(name)?.values() ?? []),
      Array.from(linePrecleanMap.get(name)?.values() ?? [])
    )

  const groups: TLGroup[] = []
  for (const [kitchenName] of kitchenBarsMap) {
    const prod = Array.from(kitchenBarsMap.get(kitchenName)!.values())
    const clean = Array.from(kitchenCleanMap.get(kitchenName)!.values())
    const preclean = Array.from(
      kitchenPrecleanMap.get(kitchenName)?.values() ?? []
    )
    const kChangeovers = Array.from(
      kitchenChangeoverMap.get(kitchenName)?.values() ?? []
    )
    const downtime = downtimeMap.get(kitchenName) ?? []
    const bars = [
      ...prod,
      ...clean,
      ...preclean,
      ...kChangeovers,
      ...downtime,
    ].sort((a, b) => a.start - b.start)
    const prodH = prod.reduce((s, b) => s + (b.end - b.start), 0)
    const kitchen: TLRowData = {
      name: kitchenName,
      bars,
      util: Math.round((prodH / TOTAL_HOURS) * 100),
      prodH,
      cleanH:
        clean.reduce((s, b) => s + (b.end - b.start), 0) +
        preclean.reduce((s, b) => s + (b.end - b.start), 0),
      coCount: 0,
      qty: prod.reduce((s, b) => s + (b.qty ?? 0), 0),
      baseBatches: prod.length,
    }
    const lines = Array.from(kitchenToLines.get(kitchenName) ?? [])
      .sort()
      .map((lineName) => {
        const klKey = `${kitchenName}||${lineName}`
        return makeLineRowFromParts(
          lineName,
          kitchenLineProdBars.get(klKey) ?? [],
          Array.from(kitchenLineCleanMap.get(klKey)?.values() ?? []),
          Array.from(kitchenLineChangeoverMap.get(klKey)?.values() ?? []),
          Array.from(kitchenLinePrecleanMap.get(klKey)?.values() ?? [])
        )
      })
    const rollUtil = lines.length
      ? Math.round(lines.reduce((s, l) => s + l.util, 0) / lines.length)
      : kitchen.util
    groups.push({ kitchen, lines, rollUtil })
  }

  const flatLines = Array.from(lineProdBars.keys()).map(makeLineRow)
  // True makespan is the latest production end across EVERY resource, not
  // just lines — a kitchen batch can finish later than any line packing it
  // (e.g. prep with no line consuming it yet), and omitting that undercounts
  // vs. the week-tile makespan (SchedulerInput.topStrip.tsx), which already
  // takes max(line_end_time, kitchen_end_time) per row.
  //
  // Zero-length bars are skipped, and that is what keeps this agreeing with
  // the tile on a consolidated run. These rows arrive already rebased, and a
  // row can be in this week for its CLEANING alone — a batch opening week 2
  // is routinely dragged into week 1 by its idle preclean. `clipRowToWeek`
  // then collapses that row's production to a zero-length interval sitting
  // on the slice edge, so counting its end would report a week that produces
  // nothing after hour 65 as running the full 168. Same rule as
  // `computeSliceMakespan`'s overlap guard (makespan.ts).
  const isRealProduction = (b: TLBarData) =>
    b.type === 'production' && b.end > b.start
  const makespan = Math.max(
    0,
    ...flatLines.flatMap((l) =>
      l.bars.filter(isRealProduction).map((b) => b.end)
    ),
    ...groups.flatMap((g) =>
      g.kitchen.bars.filter(isRealProduction).map((b) => b.end)
    )
  )
  const avgUtil = flatLines.length
    ? Math.round(flatLines.reduce((s, l) => s + l.util, 0) / flatLines.length)
    : 0
  const totalCleanH = flatLines.reduce((s, l) => s + l.cleanH, 0)
  const changeovers = flatLines.reduce((s, l) => s + l.coCount, 0)
  const totalProdHours = flatLines.reduce((s, l) => s + l.prodH, 0)
  const totalQuantity = flatLines.reduce((s, l) => s + l.qty, 0)
  const productCount = new Set(
    outputRows.map((r) => r.productCode).filter(Boolean)
  ).size
  const lineCount = flatLines.length
  const kitchenCount = groups.length

  return {
    groups,
    flatLines,
    makespan,
    avgUtil,
    totalCleanH,
    changeovers,
    totalProdHours,
    totalQuantity,
    productCount,
    lineCount,
    kitchenCount,
  }
}

// ─── Schedule CSV export ──────────────────────────────────────────────────────

// The scalar kitchen_preclean_*/line_preclean_* fields blend two distinct CIP
// triggers (see cipReasonFor's doc comment above) — a genuine idle-triggered
// preclean and a mid-run max_run_hours clean. The CSV splits them into
// separate column pairs so a reader isn't left guessing which one a given
// row's preclean actually was.
const isMaxRunHoursClean = (
  additionalCipsRaw: string | null | undefined,
  start: number | null | undefined,
  end: number | null | undefined
): boolean => cipReasonFor(additionalCipsRaw, start, end) === 'max_run_hours'

// Each row is already exactly one production block (one kitchen+line segment
// for one product) — one InputRow in, one CSV row out, no fan-out/fan-in.
const SCHEDULE_CSV_FIELDS: {
  header: string
  get: (row: InputRow) => string | number | null | undefined
}[] = [
  { header: 'Kitchen', get: (r) => r.kitchen },
  { header: 'Line', get: (r) => r.line },
  { header: 'Product Code', get: (r) => r.productCode },
  { header: 'Product Description', get: (r) => r.product_description },
  { header: 'Base Material', get: (r) => r.base_material },
  {
    header: 'Base Material Description',
    get: (r) => r.base_material_description,
  },
  { header: 'Quantity', get: (r) => r.quantity },
  { header: 'Line Run Rate', get: (r) => r.runRateLine },
  { header: 'Kitchen Start', get: (r) => r.kitchen_start_time },
  { header: 'Kitchen End', get: (r) => r.kitchen_end_time },
  {
    header: 'Kitchen Pre-clean Start',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_kitchen_cips,
        r.kitchen_preclean_start_time,
        r.kitchen_preclean_end_time
      )
        ? null
        : r.kitchen_preclean_start_time,
  },
  {
    header: 'Kitchen Pre-clean End',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_kitchen_cips,
        r.kitchen_preclean_start_time,
        r.kitchen_preclean_end_time
      )
        ? null
        : r.kitchen_preclean_end_time,
  },
  {
    header: 'Kitchen Max-run Clean Start',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_kitchen_cips,
        r.kitchen_preclean_start_time,
        r.kitchen_preclean_end_time
      )
        ? r.kitchen_preclean_start_time
        : null,
  },
  {
    header: 'Kitchen Max-run Clean End',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_kitchen_cips,
        r.kitchen_preclean_start_time,
        r.kitchen_preclean_end_time
      )
        ? r.kitchen_preclean_end_time
        : null,
  },
  { header: 'Kitchen Clean Start', get: (r) => r.kitchen_clean_start_time },
  { header: 'Kitchen Clean End', get: (r) => r.kitchen_clean_end_time },
  {
    header: 'Kitchen Changeover Start',
    get: (r) => r.kitchen_changeover_start_time,
  },
  {
    header: 'Kitchen Changeover End',
    get: (r) => r.kitchen_changeover_end_time,
  },
  { header: 'Line Start', get: (r) => r.line_start_time },
  { header: 'Line End', get: (r) => r.line_end_time },
  {
    header: 'Line Pre-clean Start',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_line_cips,
        r.line_preclean_start_time,
        r.line_preclean_end_time
      )
        ? null
        : r.line_preclean_start_time,
  },
  {
    header: 'Line Pre-clean End',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_line_cips,
        r.line_preclean_start_time,
        r.line_preclean_end_time
      )
        ? null
        : r.line_preclean_end_time,
  },
  {
    header: 'Line Max-run Clean Start',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_line_cips,
        r.line_preclean_start_time,
        r.line_preclean_end_time
      )
        ? r.line_preclean_start_time
        : null,
  },
  {
    header: 'Line Max-run Clean End',
    get: (r) =>
      isMaxRunHoursClean(
        r.additional_line_cips,
        r.line_preclean_start_time,
        r.line_preclean_end_time
      )
        ? r.line_preclean_end_time
        : null,
  },
  { header: 'Line Clean Start', get: (r) => r.line_clean_start_time },
  { header: 'Line Clean End', get: (r) => r.line_clean_end_time },
  { header: 'Line Changeover Start', get: (r) => r.line_changeover_start_time },
  { header: 'Line Changeover End', get: (r) => r.line_changeover_end_time },
]

const buildScheduleCsv = (rows: InputRow[]): string => {
  const headers = SCHEDULE_CSV_FIELDS.map((f) => f.header)
  const data = rows.map((row) => {
    const out: Record<string, string | number> = {}
    SCHEDULE_CSV_FIELDS.forEach(({ get, header }) => {
      const v = get(row)
      out[header] = v === undefined || v === null ? '' : v
    })
    return out
  })
  return Papa.unparse({ fields: headers, data })
}

/**
 * `week` and `scheduleVariant` are named in the file, not just used to pick
 * the rows: the export already followed the week and answer on screen, but
 * `FactorySchedule_<date>.csv` said neither, so two downloads taken minutes
 * apart from different weeks or answers were indistinguishable on disk.
 */
const downloadScheduleCsv = (
  rows: InputRow[],
  week: number | undefined,
  scheduleVariant: string
) => {
  const csv = buildScheduleCsv(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const weekPart = week === undefined ? '' : `W${week}_`
  link.download = `FactorySchedule_${weekPart}${scheduleVariant}_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ─── TLBar ────────────────────────────────────────────────────────────────────

const TLBar = memo(
  ({
    b,
    trackH,
    dim,
    selected,
    onEnter,
    onLeave,
    onClick,
    cleanClickable = false,
    commentCount = 0,
    commentsAllResolved = false,
  }: {
    b: TLBarData
    trackH: number
    dim?: boolean
    selected?: boolean
    onEnter?: (b: TLBarData, e: React.MouseEvent) => void
    onLeave?: () => void
    onClick?: (b: TLBarData, e: React.MouseEvent) => void
    /** Whether a clean bar's click does anything — false on an alternative
     *  answer, where removal is withheld. Passed in rather than inferred so
     *  the pointer cursor never promises an action that won't happen. */
    cleanClickable?: boolean
    // Counted rather than passed as the comments themselves, so a bar with an
    // unchanged count doesn't re-render on every refetch.
    commentCount?: number
    commentsAllResolved?: boolean
  }) => {
    const width = Math.max(0, b.end - b.start)
    if (width <= 0) return null
    const wideEnough = width >= 5
    // Carry-over cue. A bar clamped at a week boundary keeps a squared-off
    // corner there and gets a hatched cap, so "runs on from last week" reads
    // differently from "starts Monday 00:00". Applied through commonSx so
    // every bar type below picks it up, and via ::before/::after so no
    // branch has to grow a child element. `overflow: hidden` keeps the caps
    // inside the bar.
    const CUT_HATCH =
      'repeating-linear-gradient(135deg, rgba(255,255,255,0.9) 0 2px, rgba(20,20,20,0.35) 2px 4px)'
    const cutCap = {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      bottom: 0,
      width: 4,
      background: CUT_HATCH,
      zIndex: 2,
    }
    // Clean bars open the remove-clean dialog and production bars open their
    // comment thread; nothing else does anything on click, so nothing else
    // keeps the pointer affordance `commonSx` would otherwise give every bar.
    const actionable = Boolean(
      onClick && (b.anchor || (b.clean && cleanClickable))
    )
    const clickableSx = actionable ? {} : { cursor: 'default' as const }
    const commonSx = {
      position: 'absolute' as const,
      left: `${(b.start / TOTAL_HOURS) * 100}%`,
      width: `${(width / TOTAL_HOURS) * 100}%`,
      top: 3,
      height: trackH - 6,
      borderRadius: b.truncStart
        ? b.truncEnd
          ? '0'
          : '0 2px 2px 0'
        : b.truncEnd
          ? '2px 0 0 2px'
          : '2px',
      ...(b.truncStart && { '&::before': { ...cutCap, left: 0 } }),
      ...(b.truncEnd && { '&::after': { ...cutCap, right: 0 } }),
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      opacity: dim ? 0.28 : 1,
      transition: 'opacity 0.12s',
      ...clickableSx,
      boxShadow: selected ? `0 0 0 2px ${TOKENS.ink900}` : 'none',
      zIndex: selected ? 3 : 1,
    }
    const handlers = {
      onMouseEnter: (e: React.MouseEvent) => onEnter?.(b, e),
      onMouseLeave: () => onLeave?.(),
      onClick: (e: React.MouseEvent) => onClick?.(b, e),
    }

    if (b.type === 'production') {
      const isBase = b.stage === 'base'
      const fill = b.color ?? (isBase ? '#0E7C86' : TOKENS.ganttProduction)
      const edge = b.edgeColor ?? (isBase ? '#08515A' : TOKENS.blue600)
      // Kitchen-resolved fills (b.color) are now 40%-opacity pastels — white
      // text is unreadable on those, so use dark ink there and reserve
      // white for the darker, solid default fallback above.
      const labelColor = b.color ? TOKENS.ink900 : '#fff'
      const qty = b.qty ?? 0
      const qtyStr =
        qty >= 1000
          ? `${(qty / 1000).toFixed(qty >= 10000 ? 0 : 1)}k`
          : String(Math.round(qty))
      const label = isBase
        ? (b.product ?? '')
        : `${b.product ?? ''} · ${qtyStr}`
      // Mid-week reschedule provenance. Set only on a reschedule, so an
      // ordinary run keeps exactly the bar it always drew.
      //
      // The two cues pull in opposite directions on purpose: carried-over
      // history recedes (it is settled, and nothing the planner can act on),
      // while replanned work gets a full-height accent on its leading edge so
      // "what did this re-run actually change?" is answerable at a glance.
      // The accent is on the edge rather than the fill because the fill is
      // already carrying kitchen identity, and a second colour there would
      // put the two meanings in conflict.
      const provenanceSx = b.carriedOver
        ? { opacity: dim ? 0.28 : 0.5, filter: 'saturate(0.6)' }
        : b.replanned
          ? { borderLeft: `3px solid ${TOKENS.stateRunning}` }
          : {}
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            bgcolor: fill,
            borderLeft: `2px solid ${edge}`,
            backgroundImage: isBase
              ? 'repeating-linear-gradient(135deg, rgba(255,255,255,0.10) 0 6px, transparent 6px 12px)'
              : 'none',
            ...provenanceSx,
          }}
        >
          {wideEnough && label && (
            <Box
              component="span"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: 9.5,
                fontWeight: 600,
                color: labelColor,
                px: '5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                letterSpacing: '-0.02em',
              }}
            >
              {label}
            </Box>
          )}
          {commentCount > 0 && <CommentDot allResolved={commentsAllResolved} />}
        </Box>
      )
    }
    if (b.type === 'clean') {
      const fill = b.color ?? TOKENS.ganttClean
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            bgcolor: fill,
            borderLeft: b.edgeColor ? `2px solid ${b.edgeColor}` : 'none',
          }}
        >
          {wideEnough && (
            <>
              <CleaningServicesIcon
                sx={{ fontSize: 11, color: '#fff', ml: '4px', flexShrink: 0 }}
              />
              {b.product && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: '#fff',
                    px: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {b.product}
                </Box>
              )}
            </>
          )}
        </Box>
      )
    }
    if (b.type === 'preclean') {
      const fill = b.color ?? TOKENS.ganttPreClean
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            bgcolor: fill,
            borderLeft: b.edgeColor ? `2px solid ${b.edgeColor}` : 'none',
          }}
        >
          {wideEnough && (
            <>
              <CleaningServicesIcon
                sx={{
                  fontSize: 11,
                  color: TOKENS.ink900,
                  ml: '4px',
                  flexShrink: 0,
                }}
              />
              {b.product && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: TOKENS.ink900,
                    px: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {b.product}
                </Box>
              )}
            </>
          )}
        </Box>
      )
    }
    if (b.type === 'maxrunclean') {
      const fill = b.color ?? TOKENS.ganttMaxRunClean
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            bgcolor: fill,
            borderLeft: b.edgeColor ? `2px solid ${b.edgeColor}` : 'none',
          }}
        >
          {wideEnough && (
            <>
              <CleaningServicesIcon
                sx={{ fontSize: 11, color: '#fff', ml: '4px', flexShrink: 0 }}
              />
              {b.product && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: '#fff',
                    px: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {b.product}
                </Box>
              )}
            </>
          )}
        </Box>
      )
    }
    if (b.type === 'changeover') {
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            bgcolor: TOKENS.ganttChangeover,
            backgroundImage:
              'repeating-linear-gradient(135deg, rgba(255,255,255,0.22) 0 2px, transparent 2px 4px)',
          }}
        />
      )
    }
    if (b.type === 'downtime') {
      return (
        <Box
          {...handlers}
          sx={{
            ...commonSx,
            background: 'rgba(196,69,54,0.12)',
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent 0 4px, rgba(196,69,54,0.34) 4px 8px)',
            border: '1px solid rgba(196,69,54,0.55)',
            gap: '3px',
            pl: '4px',
          }}
        >
          {/* Same indicator a commented production bar carries. Downtime
              comments are stored per resource per week and narrowed to this
              window by `commentsFor`, so the dot marks the window the note
              was written on rather than every window on the resource. Without
              it the only way to find a downtime comment was to hover each
              hatched block in turn, which is why they read as lost. */}
          {commentCount > 0 && (
            <CommentDot
              allResolved={commentsAllResolved}
              accent={COMMENT_ACCENT_DOWNTIME}
            />
          )}
        </Box>
      )
    }
    return null
  }
)
TLBar.displayName = 'TLBar'

// ─── TLRowMetric ──────────────────────────────────────────────────────────────

const TLRowMetric = memo(
  ({
    util,
    prodH,
    mode = 'bar',
  }: {
    util: number
    prodH: number
    mode?: 'bar' | 'pct' | 'hours'
  }) => {
    const band = utilBand(util)
    if (mode === 'hours')
      return (
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 600,
            color: TOKENS.ink500,
          }}
        >
          {prodH.toFixed(0)}h
        </Box>
      )
    if (mode === 'pct')
      return (
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 10.5,
            fontWeight: 700,
            color: band.fg,
          }}
        >
          {util}%
        </Box>
      )
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          width: 64,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: 5,
            bgcolor: band.track,
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${Math.min(100, util)}%`,
              height: '100%',
              bgcolor: band.fg,
              borderRadius: '3px',
            }}
          />
        </Box>
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            fontWeight: 700,
            color: band.fg,
            width: 24,
            textAlign: 'right',
          }}
        >
          {util}
        </Box>
      </Box>
    )
  }
)
TLRowMetric.displayName = 'TLRowMetric'

// ─── TLAxis ───────────────────────────────────────────────────────────────────

const TLAxis = memo(({ trackW, fit }: { trackW: number; fit: boolean }) => (
  <Box
    sx={{
      display: 'flex',
      height: 34,
      position: 'sticky',
      top: 0,
      zIndex: 8,
      bgcolor: TOKENS.surface0,
      borderBottom: `2px solid ${TOKENS.line300}`,
      width: fit ? 'auto' : RAIL + trackW,
      minWidth: fit ? undefined : RAIL + trackW,
    }}
  >
    <Box
      sx={{
        width: RAIL,
        minWidth: RAIL,
        borderRight: `1px solid ${TOKENS.line200}`,
        display: 'flex',
        alignItems: 'flex-end',
        px: '12px',
        pb: '5px',
        position: 'sticky',
        left: 0,
        zIndex: 9,
        bgcolor: TOKENS.surface0,
      }}
    >
      <Box
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: TOKENS.ink400,
        }}
      >
        Resource
      </Box>
    </Box>
    <Box
      sx={{
        flex: fit ? 1 : undefined,
        width: fit ? undefined : trackW,
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {DAY_LABELS.map((d, i) => {
        const isWeekend = i >= 5
        return (
          <Box
            key={d}
            sx={{
              position: 'absolute',
              left: `${((i * 24) / TOTAL_HOURS) * 100}%`,
              width: `${(24 / TOTAL_HOURS) * 100}%`,
              top: 0,
              bottom: 0,
              borderLeft: i ? `1px solid ${TOKENS.line200}` : 'none',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              pt: '4px',
            }}
          >
            <Box
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: isWeekend ? TOKENS.ink400 : TOKENS.ink700,
                pl: '6px',
              }}
            >
              {d}
            </Box>
            <Box sx={{ display: 'flex', pb: '3px' }}>
              {[0, 6, 12, 18].map((h) => (
                <Box
                  key={h}
                  sx={{
                    flex: 1,
                    fontSize: 8,
                    color: TOKENS.ink300,
                    fontFamily: FONT_MONO,
                    pl: '6px',
                  }}
                >
                  {String(h).padStart(2, '0')}
                </Box>
              ))}
            </Box>
          </Box>
        )
      })}
    </Box>
  </Box>
))
TLAxis.displayName = 'TLAxis'

// ─── TLRow ────────────────────────────────────────────────────────────────────

const TLRow = memo(
  ({
    row,
    isHeader,
    rollUtil,
    collapsed,
    onToggle,
    rowH,
    hoverBase,
    trackW,
    fit,
    onEnter,
    onLeave,
    onBarClick,
    cleanClickable = false,
    commentIndex,
    commentWeek = 1,
    headerIcon = 'kitchen',
    rowIcon = 'line',
    selectedRun = null,
  }: {
    row: TLRowData
    isHeader: boolean
    rollUtil?: number
    collapsed?: boolean
    onToggle?: () => void
    rowH: number
    hoverBase: string | null
    trackW: number
    fit: boolean
    onEnter?: (b: TLBarData, e: React.MouseEvent) => void
    onLeave?: () => void
    onBarClick?: (b: TLBarData, e: React.MouseEvent) => void
    cleanClickable?: boolean
    /** Every comment on this week keyed by anchor — see ScheduleComments.anchor. */
    commentIndex?: Map<string, SchedulerComment[]>
    commentWeek?: number
    /** What a header row stands for — a kitchen (By kitchen), a cleaner
     *  (Cleaning) or a line (Line, expanded into its products). Only changes
     *  the icon. */
    headerIcon?: 'kitchen' | 'cleaner' | 'line'
    /** What a non-header row stands for. Only changes the icon. */
    rowIcon?: 'line' | 'product'
    /** The run selected for the Model description — its bar is outlined. */
    selectedRun?: MDRunFocus | null
  }) => {
    const displayUtil = isHeader ? (rollUtil ?? row.util) : row.util
    const band = utilBand(displayUtil)
    // A product row's label is "code · name"; split it so the name can lead.
    const labelSep = isHeader ? -1 : row.name.indexOf(' · ')
    const labelParts =
      labelSep < 0
        ? { title: row.name, code: '' }
        : {
            title: row.name.slice(labelSep + 3),
            code: row.name.slice(0, labelSep),
          }
    const totalW = fit ? 'auto' : RAIL + trackW

    return (
      <Box
        sx={{
          display: 'flex',
          height: rowH,
          borderBottom: `1px solid ${TOKENS.line100}`,
          width: totalW,
          minWidth: fit ? undefined : totalW,
          bgcolor: isHeader ? TOKENS.panelHead : TOKENS.surface0,
        }}
      >
        {/* Sticky rail */}
        <Box
          sx={{
            width: RAIL,
            minWidth: RAIL,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            position: 'sticky',
            left: 0,
            zIndex: 5,
            pl: isHeader ? '8px' : '26px',
            pr: '10px',
            borderRight: `1px solid ${TOKENS.line200}`,
            borderLeft: isHeader ? 'none' : `2px solid ${band.fg}`,
            bgcolor: isHeader ? TOKENS.panelHead : TOKENS.surface0,
          }}
        >
          {isHeader && (
            <Box
              component="button"
              onClick={onToggle}
              sx={{
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                p: 0,
                display: 'flex',
                color: TOKENS.ink500,
                flexShrink: 0,
              }}
            >
              {collapsed ? (
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
          )}
          {isHeader ? (
            headerIcon === 'cleaner' ? (
              <CleaningServicesIcon
                sx={{
                  fontSize: 13,
                  color: TOKENS.stateComplete,
                  flexShrink: 0,
                }}
              />
            ) : headerIcon === 'line' ? (
              <PrecisionManufacturingIcon
                sx={{
                  fontSize: 13,
                  color: TOKENS.stateComplete,
                  flexShrink: 0,
                }}
              />
            ) : (
              <SoupKitchenIcon
                sx={{
                  fontSize: 13,
                  color: TOKENS.stateComplete,
                  flexShrink: 0,
                }}
              />
            )
          ) : rowIcon === 'product' ? (
            <InventoryIcon
              sx={{ fontSize: 13, color: TOKENS.ink400, flexShrink: 0 }}
            />
          ) : (
            <PrecisionManufacturingIcon
              sx={{ fontSize: 13, color: TOKENS.ink400, flexShrink: 0 }}
            />
          )}
          <Box
            sx={{ flex: 1, minWidth: 0 }}
            title={
              row.description
                ? `${row.name}
${row.description}`
                : row.name
            }
          >
            <Box
              sx={{
                fontSize: isHeader ? 11.5 : 11,
                fontWeight: isHeader ? 700 : 600,
                color: TOKENS.ink900,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {/* "code · name" (`productLabel`): the NAME is what a planner
                  recognises, so it is the bold first line and the code moves
                  to the second, ahead of the route. */}
              {labelParts.title}
            </Box>
            {(labelParts.code || row.description) && (
              <Box
                sx={{
                  fontSize: 9,
                  color: TOKENS.ink400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.1,
                  mt: '1px',
                }}
              >
                {labelParts.code && (
                  <Box
                    component="span"
                    sx={{ fontFamily: FONT_MONO, color: TOKENS.ink500 }}
                  >
                    {labelParts.code}
                  </Box>
                )}
                {labelParts.code && row.description ? ' · ' : ''}
                {row.description}
              </Box>
            )}
          </Box>
          <TLRowMetric util={displayUtil} prodH={row.prodH} />
        </Box>

        {/* Track */}
        <Box
          sx={{
            flex: fit ? 1 : undefined,
            width: fit ? undefined : trackW,
            flexShrink: 0,
            position: 'relative',
            bgcolor: isHeader ? 'rgba(14,124,134,0.05)' : TOKENS.ganttIdle,
            overflow: 'hidden',
          }}
        >
          {/* Weekend shading */}
          {[5, 6].map((d) => (
            <Box
              key={d}
              sx={{
                position: 'absolute',
                left: `${((d * 24) / TOTAL_HOURS) * 100}%`,
                width: `${(24 / TOTAL_HOURS) * 100}%`,
                top: 0,
                bottom: 0,
                bgcolor: 'rgba(14,27,34,0.025)',
              }}
            />
          ))}
          {/* Day separators */}
          {[1, 2, 3, 4, 5, 6].map((d) => (
            <Box
              key={d}
              sx={{
                position: 'absolute',
                left: `${((d * 24) / TOTAL_HOURS) * 100}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: TOKENS.line200,
              }}
            />
          ))}
          {/* Bars */}
          {row.bars.map((b, i) => {
            const dim =
              hoverBase != null &&
              b.type === 'production' &&
              (b.product?.split('-')[0] ?? '') !== hoverBase
            // The window is passed for every bar; `commentsFor` only narrows
            // on it for downtime, because a production anchor must keep
            // ignoring times that the optimizer moves on every run.
            const barComments = commentsFor(
              commentIndex,
              b.anchor,
              commentWeek,
              {
                startTime: b.start,
                endTime: b.end,
              }
            )
            return (
              <TLBar
                key={i}
                b={b}
                trackH={rowH}
                dim={dim}
                selected={isSelectedBar(b, selectedRun)}
                onEnter={onEnter}
                onLeave={onLeave}
                onClick={onBarClick}
                cleanClickable={cleanClickable}
                commentCount={barComments.length}
                commentsAllResolved={
                  barComments.length > 0 && barComments.every((c) => c.resolved)
                }
              />
            )
          })}
        </Box>
      </Box>
    )
  }
)
TLRow.displayName = 'TLRow'

// ─── TLLegend ─────────────────────────────────────────────────────────────────

const TLLegend = memo(({ showReschedule }: { showReschedule?: boolean }) => {
  const items = [
    { color: TOKENS.ganttClean, label: 'CIP clean' },
    { color: TOKENS.ganttPreClean, label: 'CIP pre-clean' },
    { color: TOKENS.ganttMaxRunClean, label: 'Max-run clean' },
    {
      color: TOKENS.ganttChangeover,
      label: 'Changeover',
      hatch: '135deg',
      alert: false,
    },
    {
      color: TOKENS.ganttDowntime,
      label: 'Downtime',
      hatch: '45deg',
      alert: true,
    },
  ]
  // Base material / packed product bars are now colored per kitchen (see
  // kitchenFill/kitchenEdge) rather than a single fixed color, so a static
  // swatch no longer means anything — show the actual kitchen → color
  // mapping on hover instead, from the same cache the bars are painted from.
  const kitchenColors = getKitchenColorCache()
  const kitchenEntries = Object.entries(kitchenColors).sort(([a], [b]) =>
    a.localeCompare(b)
  )
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
      }}
    >
      {kitchenEntries.length > 0 && (
        <Tooltip
          placement="top-start"
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {kitchenEntries.map(([name, color]) => (
                <Box
                  key={name}
                  sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      // Same 40% opacity as the bars themselves (kitchenFill)
                      // so the legend swatch matches what's actually on the
                      // chart, not the raw glaring palette.
                      bgcolor: adjustOpacity(color, 0.4),
                      border: `1px solid ${adjustOpacity(color, 0.9)}`,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ fontSize: 11 }}>{name}</Box>
                </Box>
              ))}
            </Box>
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: 'default',
            }}
          >
            <SoupKitchenIcon sx={{ fontSize: 13, color: TOKENS.ink400 }} />
            <Box
              sx={{
                fontSize: 10.5,
                color: TOKENS.ink500,
                fontWeight: 500,
                textDecoration: 'underline dotted',
                textUnderlineOffset: '2px',
              }}
            >
              Kitchen colors
            </Box>
          </Box>
        </Tooltip>
      )}
      {items.map((it) => (
        <Box
          key={it.label}
          sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}
        >
          <Box
            sx={{
              width: 16,
              height: 11,
              borderRadius: '2px',
              bgcolor: it.hatch
                ? it.alert
                  ? 'rgba(196,69,54,0.12)'
                  : 'rgba(110,133,151,0.12)'
                : it.color,
              backgroundImage: it.hatch
                ? `repeating-linear-gradient(${it.hatch}, ${it.alert ? 'rgba(196,69,54,0.4)' : 'rgba(110,133,151,0.5)'} 0 2px, transparent 2px 4px)`
                : 'none',
              border: it.hatch
                ? `1px solid ${it.alert ? 'rgba(196,69,54,0.5)' : 'rgba(110,133,151,0.5)'}`
                : 'none',
            }}
          />
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink500, fontWeight: 500 }}>
            {it.label}
          </Box>
        </Box>
      ))}
      {/* Only on a mid-week reschedule. On an ordinary run no bar carries
          either cue, and a legend entry for a distinction the chart is not
          making reads as something the planner has failed to find. */}
      {showReschedule && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Box
              sx={{
                width: 16,
                height: 11,
                borderRadius: '2px',
                bgcolor: TOKENS.ganttProduction,
                borderLeft: `3px solid ${TOKENS.stateRunning}`,
              }}
            />
            <Box sx={{ fontSize: 10.5, color: TOKENS.ink500, fontWeight: 500 }}>
              Rescheduled
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Box
              sx={{
                width: 16,
                height: 11,
                borderRadius: '2px',
                bgcolor: TOKENS.ganttProduction,
                opacity: 0.5,
                filter: 'saturate(0.6)',
              }}
            />
            <Box sx={{ fontSize: 10.5, color: TOKENS.ink500, fontWeight: 500 }}>
              Already run
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
})
TLLegend.displayName = 'TLLegend'

// ─── TLTooltip ────────────────────────────────────────────────────────────────

/**
 * Confirmation for removing one clean from the schedule.
 *
 * Names the rule behind the clean rather than asking a bare "are you sure":
 * `end_of_use` and `idle_preclean` are the two the engine itself treats as
 * flexible, while `material_change` and `max_run_hours` exist because a rule
 * table said so — removing one of those leaves a schedule the engine would
 * never emit, and the planner should see which rule that is before they do it.
 * A clean carrying no recorded `reason` (a row with only the promoted scalar
 * copy and no `additional_*_cips`) is reported as unknown rather than assumed
 * safe.
 *
 * `rowCount` is shown because a clean is shared: one physical CIP is stamped
 * on every row of its batch, so a single removal edits several rows.
 */
const RemoveCleanDialog = memo(
  ({
    ref_,
    reason,
    rowCount,
    busy,
    onCancel,
    onConfirm,
  }: {
    ref_: CleanRef | null
    reason: string | undefined
    rowCount: number
    busy: boolean
    onCancel: () => void
    onConfirm: () => void
  }) => {
    if (!ref_) return null
    const risk = cleanRemovalRisk(reason)
    const mandated = risk.kind === 'mandated'
    const hours = (ref_.end - ref_.start).toFixed(1)
    return (
      <Dialog
        open
        onClose={busy ? undefined : onCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: '18px 20px 16px', bgcolor: TOKENS.surface0 }}>
          <Box sx={{ ...TYPE.h2, mb: '4px' }}>Remove this clean?</Box>
          <Box
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 11,
              color: TOKENS.ink500,
              mb: '12px',
            }}
          >
            {ref_.resource} · {ref_.kind === 'preclean' ? 'pre-clean' : 'clean'}{' '}
            · {ref_.start.toFixed(1)}h–{ref_.end.toFixed(1)}h ({hours}h)
          </Box>

          <Box
            sx={{
              p: '10px 12px',
              mb: '12px',
              borderRadius: '3px',
              border: `1px solid ${mandated ? TOKENS.ganttDowntime : TOKENS.line200}`,
              bgcolor: mandated ? 'rgba(196,69,54,0.06)' : TOKENS.surface50,
            }}
          >
            {risk.kind === 'unknown' ? (
              <Box sx={{ fontSize: 11.5, color: TOKENS.ink700 }}>
                This schedule does not record why this clean was placed, so its
                effect on the rules cannot be checked here.
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: mandated ? TOKENS.ganttDowntime : TOKENS.ink400,
                    mb: '3px',
                  }}
                >
                  {mandated ? `Required by ${risk.rule}` : 'Discretionary'}
                </Box>
                <Box sx={{ fontSize: 11.5, color: TOKENS.ink700 }}>
                  {risk.note}
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ fontSize: 11, color: TOKENS.ink400, mb: '14px' }}>
            {rowCount === 0
              ? 'This clean is no longer in the stored schedule — it may have been re-run since this view loaded. Nothing will be changed.'
              : rowCount === 1
                ? 'Edits 1 schedule row. The change is saved to this schedule and will be lost the next time it is re-run.'
                : `Edits ${rowCount} schedule rows — this clean is shared across the batch. The change is saved to this schedule and will be lost the next time it is re-run.`}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <PillButton kind="subtle" onClick={onCancel} disabled={busy}>
              Cancel
            </PillButton>
            <PillButton
              kind="primary"
              onClick={onConfirm}
              disabled={busy || rowCount === 0}
            >
              {busy ? 'Removing…' : mandated ? 'Remove anyway' : 'Remove'}
            </PillButton>
          </Box>
        </Box>
      </Dialog>
    )
  }
)
RemoveCleanDialog.displayName = 'RemoveCleanDialog'

const TLTooltip = memo(
  ({
    data,
    comments,
  }: {
    data: TLHoverTip
    /** Comments on the hovered bar — empty for bar types that can't carry one. */
    comments: SchedulerComment[]
  }) => {
    if (!data) return null
    const { b, x, y } = data
    const isBase = b.type === 'production' && b.stage === 'base'
    const isProd = b.type === 'production'
    const isDown = b.type === 'downtime'
    const title = isBase
      ? 'Base material'
      : isProd
        ? 'Production'
        : isDown
          ? 'Downtime'
          : b.type === 'clean'
            ? 'CIP clean'
            : b.type === 'preclean'
              ? 'CIP pre-clean'
              : b.type === 'maxrunclean'
                ? 'Max-run clean'
                : 'Changeover'
    const dotColor = isBase
      ? '#0E7C86'
      : isProd
        ? TOKENS.ganttProduction
        : isDown
          ? TOKENS.ganttDowntime
          : b.type === 'clean'
            ? (b.color ?? TOKENS.ganttClean)
            : b.type === 'preclean'
              ? (b.color ?? TOKENS.ganttPreClean)
              : b.type === 'maxrunclean'
                ? (b.color ?? TOKENS.ganttMaxRunClean)
                : TOKENS.ganttChangeover
    const dur = b.end - b.start
    const durStr = Number.isInteger(dur) ? `${dur}h` : `${dur.toFixed(1)}h`
    const dayH = (h: number) =>
      `${DAY_LABELS[Math.floor(h / 24) % 7]} ${String(Math.floor(h % 24)).padStart(2, '0')}:00`

    return (
      <Box
        sx={{
          position: 'fixed',
          left: Math.min(x + 14, (window.innerWidth ?? 1200) - 230),
          top: y + 14,
          zIndex: 200,
          pointerEvents: 'none',
          bgcolor: TOKENS.ink900,
          color: '#fff',
          borderRadius: '4px',
          p: '9px 11px',
          width: 206,
          boxShadow: '0 6px 22px rgba(14,27,34,0.16)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}
        >
          <Box
            sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: dotColor }}
          />
          <Box sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {title}
          </Box>
        </Box>
        {/* Mid-week reschedule provenance. Present only when the schedule on
            screen IS a reschedule, and then on every production bar — saying
            it on one kind and staying silent on the other would leave the
            silence ambiguous. Worded as what the planner can do about it
            rather than as the field name: carried-over work is settled, so
            re-running changes nothing about it. */}
        {(b.carriedOver || b.replanned) && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              mb: '7px',
              px: '5px',
              py: '2px',
              borderRadius: '3px',
              bgcolor: b.replanned
                ? 'rgba(217,119,6,0.22)'
                : 'rgba(255,255,255,0.08)',
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: b.replanned
                  ? TOKENS.stateRunning
                  : 'rgba(255,255,255,0.45)',
              }}
            />
            <Box
              sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.02em' }}
            >
              {b.replanned ? 'RESCHEDULED' : 'ALREADY RUN'}
            </Box>
          </Box>
        )}
        {(b.carriedOver || b.replanned) && (
          <Box
            sx={{
              fontSize: 9.5,
              color: 'rgba(255,255,255,0.38)',
              mt: '-4px',
              mb: '7px',
            }}
          >
            {b.replanned
              ? 'Newly planned by this re-run'
              : 'Ran before the cut-off — carried over unchanged'}
          </Box>
        )}
        {b.type === 'changeover' && changeoverWhat(b.changeoverReason) && (
          <Box sx={{ mb: '7px' }}>
            <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.72)' }}>
              {changeoverWhat(b.changeoverReason)}
            </Box>
            {parseChangeoverReason(b.changeoverReason).heldResource && (
              // Named because this is the half that costs the plant something
              // beyond this line — the held resource is what stops the sibling
              // line producing. See FORMAT_CHANGEOVER.md §3.
              <Box
                sx={{
                  fontSize: 9.5,
                  color: 'rgba(255,255,255,0.38)',
                  mt: '2px',
                }}
              >
                holds {parseChangeoverReason(b.changeoverReason).heldResource}
              </Box>
            )}
          </Box>
        )}
        {(isProd ||
          b.type === 'clean' ||
          b.type === 'preclean' ||
          b.type === 'maxrunclean') &&
          (b.productDescription || b.product) && (
            <Box sx={{ mb: '7px' }}>
              <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.72)' }}>
                {b.productDescription || b.product}
              </Box>
              {b.productDescription && b.product && (
                <Box
                  sx={{
                    fontSize: 9.5,
                    color: 'rgba(255,255,255,0.38)',
                    mt: '2px',
                  }}
                >
                  {b.product}
                </Box>
              )}
            </Box>
          )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '3px 10px',
            fontSize: 10.5,
          }}
        >
          {/* Line leads, per the slide's layout: on a packing bar the row
              you are hovering is already the line in one grouping and the
              kitchen in another, so naming it removes the ambiguity before
              any of the numbers are read. */}
          {isProd && b.lineName && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Line</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {b.lineName}
              </Box>
            </>
          )}
          <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Window</Box>
          <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
            {dayH(b.start)} → {dayH(b.end)}
          </Box>
          <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Duration</Box>
          <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>{durStr}</Box>
          {(b.truncStart || b.truncEnd) && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Spans</Box>
              <Box sx={{ textAlign: 'right' }}>
                {b.truncStart && b.truncEnd
                  ? 'the whole week'
                  : b.truncStart
                    ? 'from previous week'
                    : 'into next week'}
              </Box>
            </>
          )}
          {isProd && b.qty != null && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Quantity</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {Math.round(b.qty).toLocaleString()}
              </Box>
            </>
          )}
          {isProd && b.rate != null && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Rate</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {b.rate}/h
              </Box>
            </>
          )}
          {isProd && b.prFactor && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>PR</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {b.prFactor}
              </Box>
            </>
          )}
          {isProd && b.kitchenName && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Kitchen</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {b.kitchenName}
              </Box>
            </>
          )}
          {isProd && b.kitchenRate != null && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Kitchen rate</Box>
              <Box sx={{ fontFamily: FONT_MONO, textAlign: 'right' }}>
                {b.kitchenRate}/h
              </Box>
            </>
          )}
          {isProd && b.inputProduct && (
            <>
              <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>Input product</Box>
              <Box sx={{ textAlign: 'right' }}>{b.inputProduct}</Box>
            </>
          )}
        </Box>
        {/* This tip follows the mouse with `pointerEvents: 'none'`, so it can
          only report comments — the bar's own click opens the thread. */}
        {b.anchor && <CommentHoverBlock comments={comments} />}
      </Box>
    )
  }
)
TLTooltip.displayName = 'TLTooltip'

/** The Model description selection for a production bar, or `null` for any
 *  other bar. The anchor carries the identity (line + product code, or kitchen
 *  + base material) the same way in every grouping, which is why it is used
 *  rather than the row the bar happens to sit in. */
const runFocusFor = (
  b: TLBarData,
  weekBaseHour: number | null
): MDRunFocus | null => {
  if (
    b.type !== 'production' ||
    !b.anchor ||
    b.anchor.kind === 'downtime' ||
    !b.stage
  )
    return null
  const base = weekBaseHour ?? 0
  const what = b.productDescription || b.anchor.productCode
  return {
    stage: b.stage,
    resource: b.anchor.resourceName,
    code: b.anchor.productCode,
    start: b.start + base,
    end: b.end + base,
    barStart: b.start,
    barEnd: b.end,
    label: `${what} on ${b.anchor.resourceName}`,
  }
}

const isSelectedBar = (
  b: TLBarData,
  f: MDRunFocus | null | undefined
): boolean =>
  !!f &&
  b.type === 'production' &&
  b.stage === f.stage &&
  b.anchor?.resourceName === f.resource &&
  b.anchor?.productCode === f.code &&
  b.start === f.barStart &&
  b.end === f.barEnd

/** "901621168 · FMCD TK DIP…" — a product as a planner names it in the rail
 *  and the Product filter. The code comes first because it is what they
 *  search and sort by; a blank or code-only description is not repeated. */
const productLabel = (code: string, description?: string | null): string =>
  description && description !== code
    ? code
      ? `${code} · ${description}`
      : description
    : code

/** A timeline row's identity in the saved custom order. */
const tlRowId = (r: TLRowData): string => r.id ?? r.name

// ─── TimelineTab ──────────────────────────────────────────────────────────────

export const TimelineTab = memo(
  ({
    outputRows: absoluteRows,
    weekBaseHour = null,
    selectedWeek,
    productionRequirements,
    syncGroups,
    downtimeData,
    headerRight,
    midWeekRunning,
    midWeekProgress,
    scheduleId,
    iteration,
    outputVariant,
    onOutputVariantChange,
    hasReschedule,
    selectedRun = null,
    onSelectRun,
    onExplainRun,
  }: {
    /** ABSOLUTE consolidated-horizon rows — rebased below, not rendered
     *  directly. See `rebaseRowsToWeek`. */
    outputRows: InputRow[]
    /** From `weekBaseHourFor` — `null` for a single-week schedule, whose
     *  rows must not be clipped. 0 is week 1 of a consolidated run, which
     *  still gets clipped at hour 168. */
    weekBaseHour?: number | null
    /** Display week number. Only used to name the CSV export — the rows
     *  themselves are already scoped to it by the caller's fetch. */
    selectedWeek?: number
    productionRequirements?: ProductionRequirementRowType[]
    syncGroups?: SynchronizedLinesRowType[]
    downtimeData?: WorkstationDownTimeRowType[]
    headerRight?: React.ReactNode
    midWeekRunning?: boolean
    midWeekProgress?: SchedulerProgress | null
    // Needed (alongside iteration) for comments and the schedule
    // actions on the version currently open.
    scheduleId?: string
    // Shown in the header so it's never ambiguous which iteration/variant
    // this timeline is displaying.
    iteration?: string
    outputVariant?: 'original' | 'latest'
    /** Switches which schedule the timeline shows. Omitted = no toggle,
     *  which is how every call site behaved before the control existed. */
    onOutputVariantChange?: (variant: 'original' | 'latest') => void
    /** Whether this iteration has a mid-week reschedule at all (the server's
     *  answer to a 'latest' request, whatever is on screen). NOT the same
     *  question as `outputVariant === 'latest'`: "latest" falls back to the
     *  original when there is no reschedule, so the badge needs the server's
     *  answer rather than the request's. And NOT "are the rows on screen
     *  rescheduled": asked that way, the toggle disappeared as soon as the
     *  planner switched to the original, leaving no way back. */
    hasReschedule?: boolean
    /** The run selected for the Model description; its bar is outlined. */
    selectedRun?: MDRunFocus | null
    /** Clicking a production bar selects it for the Model description. */
    onSelectRun?: (f: MDRunFocus) => void
    /** "Explain in Model description" on the clicked bar's pop-up: select it
     *  AND open that tab. Omitted = no such action. */
    onExplainRun?: (f: MDRunFocus) => void
  }) => {
    const scheduleVariant = useContext(ScheduleVariantContext)
    const outputRows = useMemo(
      () => rebaseRowsToWeek(absoluteRows, weekBaseHour),
      [absoluteRows, weekBaseHour]
    )
    const [grouping, setGrouping] = useState<
      'kitchen' | 'flat' | 'combined' | 'cleaning'
    >('combined')
    const [search, setSearch] = useState('')
    const [kitchenFilter, setKitchenFilter] = useState<string[]>([])
    const [lineFilter, setLineFilter] = useState<string[]>([])
    const [productFilter, setProductFilter] = useState<string[]>([])
    const [baseMaterialFilter, setBaseMaterialFilter] = useState<string[]>([])
    const [locationFilter, setLocationFilter] = useState<string[]>([])
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
    const [hoverTip, setHoverTip] = useState<TLHoverTip>(null)
    const [hoverBase, setHoverBase] = useState<string | null>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)

    useEffect(() => {
      if (!isFullscreen) return
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsFullscreen(false)
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [isFullscreen])

    // The timeline always fits its container — the old comfortable/detailed
    // fixed-zoom modes were removed as unnecessary.
    const fit = true
    const trackW = 0

    // Distinct values for the Kitchen/Line/Product/Base material selectors —
    // always derived from the full unfiltered outputRows so picking one
    // filter never makes the others' option lists shrink.
    // Resource -> site location. An output row's own `site_location` is its
    // LINE's location only (postprocess looks it up by line id), and it comes
    // from the downtime table's `site_location` column in the first place — so
    // the downtime rows are the source for every resource, kitchens included,
    // with the row value as the fallback for a line the table does not list.
    const resourceLocation = useMemo(() => {
      const m = new Map<string, string>()
      for (const dt of downtimeData ?? [])
        if (dt.resource && dt.site_location)
          m.set(dt.resource, dt.site_location)
      for (const r of outputRows)
        if (r.line && r.site_location && !m.has(r.line))
          m.set(r.line, r.site_location)
      return m
    }, [downtimeData, outputRows])

    const filterOptions = useMemo(() => {
      // Keyed by the code (the actual filter value / unique identifier),
      // valued by the human-readable name shown in the dropdown — falls
      // back to the code itself if a row has no name for it.
      const kitchens = new Map<string, string>()
      const lines = new Map<string, string>()
      const products = new Map<string, string>()
      const baseMaterials = new Map<string, string>()
      const locations = new Map<string, string>()
      for (const r of outputRows) {
        for (const res of [r.kitchen, r.line]) {
          const loc = res ? resourceLocation.get(res) : undefined
          if (loc) locations.set(loc, loc)
        }
        if (r.kitchen) kitchens.set(r.kitchen, r.kitchen)
        if (r.line) lines.set(r.line, r.line)
        // Labelled code + name so the dropdown's own search (which matches
        // the label) finds a product by either.
        if (r.productCode)
          products.set(
            r.productCode,
            productLabel(r.productCode, r.product_description)
          )
        if (r.base_material)
          baseMaterials.set(
            r.base_material,
            r.base_material_description || r.base_material
          )
      }
      const sorted = (m: Map<string, string>) =>
        Array.from(m, ([value, label]) => ({ value, label })).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      return {
        kitchens: sorted(kitchens),
        lines: sorted(lines),
        products: sorted(products),
        baseMaterials: sorted(baseMaterials),
        locations: sorted(locations),
      }
    }, [outputRows, resourceLocation])

    // Filters outputRows by the Kitchen/Line/Product/Base material selectors
    // (each a multi-select "is one of the checked values", combined with
    // AND across dimensions) plus a free-text match against kitchen, line,
    // product and CIP identifiers — feeding every grouping mode (and the
    // KPIs, which derive from the same filtered rows) so the filter applies
    // consistently across By kitchen / Line / Combined / Cleaning.
    const filteredOutputRows = useMemo(() => {
      const q = search.trim().toLowerCase()
      return outputRows.filter((r) => {
        if (kitchenFilter.length && !kitchenFilter.includes(r.kitchen))
          return false
        if (lineFilter.length && !lineFilter.includes(r.line)) return false
        if (productFilter.length && !productFilter.includes(r.productCode))
          return false
        if (
          baseMaterialFilter.length &&
          !baseMaterialFilter.includes(r.base_material)
        )
          return false
        // A row sits at a location if its kitchen OR its line does — a batch
        // made in one hall and packed in another belongs to both, and
        // dropping it from either would hide half the work at that site.
        if (
          locationFilter.length &&
          !locationFilter.includes(resourceLocation.get(r.kitchen) ?? '') &&
          !locationFilter.includes(resourceLocation.get(r.line) ?? '')
        )
          return false
        if (!q) return true
        return [
          r.kitchen,
          r.line,
          r.productCode,
          r.product_description,
          r.base_material,
          r.base_material_description,
          r.kitchenCIP,
          r.lineCIP,
          r.kitchenPrecleanCIP,
          r.linePrecleanCIP,
          r.site_location,
        ].some((v) => v && String(v).toLowerCase().includes(q))
      })
    }, [
      outputRows,
      search,
      kitchenFilter,
      lineFilter,
      productFilter,
      baseMaterialFilter,
      locationFilter,
      resourceLocation,
    ])

    const {
      groups,
      flatLines,
      makespan,
      avgUtil,
      totalCleanH,
      changeovers,
      totalProdHours,
      totalQuantity,
      productCount,
      lineCount,
      kitchenCount,
    } = useMemo(
      () => buildTimelineData(filteredOutputRows, downtimeData),
      [filteredOutputRows, downtimeData]
    )

    // Line-side clean/preclean/max-run-clean ARE shown here (scoped to
    // exactly one line per the decoder's per-line CIP search — never
    // duplicated across rows) — but kitchen-side cleaning deliberately is
    // NOT: a single kitchen batch can feed several lines at once, and
    // kitchen_clean_*/kitchen_preclean_* are computed once per batch then
    // copied onto every line-row it produced, so showing them here would
    // duplicate the same physical kitchen clean under every line that batch
    // fed. By kitchen already shows kitchen-side cleaning unambiguously.
    const combinedRows = useMemo((): TLRowData[] => {
      // Same rule as `buildTimelineData`'s `provenance`, asked of the UNFILTERED
      // rows: whether this result is a reschedule is a fact about the run, not
      // about whichever kitchen/line the planner is currently filtering to.
      const isReschedule = outputRows.some((r) => r.fixed === true)
      const provenance = (
        row: InputRow
      ): Pick<TLBarData, 'carriedOver' | 'replanned'> =>
        !isReschedule
          ? {}
          : row.fixed === true
            ? { carriedOver: true }
            : { replanned: true }
      type CombinedEntry = {
        productCode: string
        productDescription: string
        line: string
        kitchen: string
        seen: Set<string>
        bars: TLBarData[]
        prodH: number
        cleanH: number
        qty: number
      }
      const groups = new Map<string, CombinedEntry>()

      // Downtime is a property of the line resource itself (a separate
      // per-resource table, not a per-row field) — safe to attribute to
      // every product using that line, same as line clean/preclean.
      const lineDowntimeMap = new Map<string, [number, number][]>()
      for (const dt of downtimeData ?? []) {
        if (!dt.resource || !dt.downTime) continue
        const intervals = parseDowntimeStr(dt.downTime)
        if (intervals.length) lineDowntimeMap.set(dt.resource, intervals)
      }

      for (const row of filteredOutputRows) {
        if (!row.productCode || !row.line || !row.kitchen) continue
        const key = `${row.productCode}||${row.line}||${row.kitchen}`
        if (!groups.has(key))
          groups.set(key, {
            productCode: row.productCode,
            productDescription: row.product_description ?? '',
            line: row.line,
            kitchen: row.kitchen,
            seen: new Set(),
            bars: [],
            prodH: 0,
            cleanH: 0,
            qty: 0,
          })
        const g = groups.get(key)!

        const addBar = (
          barKey: string,
          bar: TLBarData,
          dProd?: number,
          dClean?: number
        ) => {
          if (g.seen.has(barKey)) return
          g.seen.add(barKey)
          g.bars.push(bar)
          if (dProd != null) {
            g.prodH += dProd
            g.qty += bar.qty ?? 0
          }
          if (dClean != null) g.cleanH += dClean
        }

        if (
          row.line_start_time != null &&
          row.line_end_time != null &&
          row.line_end_time > row.line_start_time
        )
          addBar(
            `p|${row.line_start_time}|${row.line_end_time}`,
            {
              start: row.line_start_time,
              end: row.line_end_time,
              ...truncOf(row, 'line_start_time'),
              type: 'production',
              stage: 'pack',
              product: row.productCode ?? '',
              productDescription: row.product_description ?? '',
              qty: row.quantity ?? 0,
              rate: row.runRateLine ?? null,
              ...lineDetail(row),
              color: kitchenFill(row.kitchen),
              edgeColor: kitchenEdge(row.kitchen),
              anchor: {
                resourceName: row.line,
                resourceType: 'line',
                productCode: row.productCode ?? '',
              },
              ...provenance(row),
            },
            row.line_end_time - row.line_start_time
          )

        if (
          row.line_clean_start_time != null &&
          row.line_clean_end_time != null &&
          row.line_clean_end_time > row.line_clean_start_time
        )
          addBar(
            `c|${row.line_clean_start_time}|${row.line_clean_end_time}`,
            {
              start: row.line_clean_start_time,
              end: row.line_clean_end_time,
              ...truncOf(row, 'line_clean_start_time'),
              type: 'clean',
              clean: {
                side: 'line',
                resource: row.line,
                kind: 'clean',
                start: row.line_clean_start_time,
                end: row.line_clean_end_time,
              },
            },
            undefined,
            row.line_clean_end_time - row.line_clean_start_time
          )

        if (
          row.line_preclean_start_time != null &&
          row.line_preclean_end_time != null &&
          row.line_preclean_end_time > row.line_preclean_start_time
        )
          addBar(
            `pc|${row.line_preclean_start_time}|${row.line_preclean_end_time}`,
            {
              start: row.line_preclean_start_time,
              end: row.line_preclean_end_time,
              ...truncOf(row, 'line_preclean_start_time'),
              clean: {
                side: 'line',
                resource: row.line,
                kind: 'preclean',
                start: row.line_preclean_start_time,
                end: row.line_preclean_end_time,
              },
              type:
                cipReasonFor(
                  row.additional_line_cips,
                  row.line_preclean_start_time,
                  row.line_preclean_end_time
                ) === 'max_run_hours'
                  ? 'maxrunclean'
                  : 'preclean',
            },
            undefined,
            row.line_preclean_end_time - row.line_preclean_start_time
          )

        // Changeovers, from the EVENT ARRAY first. This view builds its own
        // bars rather than going through `buildTimelineData`, and it used to
        // read the scalar pair alone — which is only the *primary* changeover
        // (the latest one ending before production) and carries no `reason`.
        // Two consequences, both visible against a real schedule: a run owing
        // both an equipment switch and a format change drew only one bar, and
        // the tooltip could not say what either was for, so Combined showed
        // "Window / Duration" where Line showed "A to B / holds …".
        //
        // Keyed by window so the scalar fallback below cannot double-add an
        // event the array already covered.
        for (const evt of parseChangeoverEvents(
          row.additional_line_changeovers
        ))
          if (evt.end > evt.start)
            addBar(`co|${evt.start}|${evt.end}`, {
              start: evt.start,
              end: evt.end,
              type: 'changeover',
              changeoverReason: evt.reason,
            })

        // Scalar fallback — still needed for rows whose changeover predates
        // the event array, or where the array is absent.
        if (
          row.line_changeover_start_time != null &&
          row.line_changeover_end_time != null &&
          row.line_changeover_end_time > row.line_changeover_start_time
        )
          addBar(
            `co|${row.line_changeover_start_time}|${row.line_changeover_end_time}`,
            {
              start: row.line_changeover_start_time,
              end: row.line_changeover_end_time,
              ...truncOf(row, 'line_changeover_start_time'),
              type: 'changeover',
            }
          )

        for (const [start, end] of lineDowntimeMap.get(row.line) ?? [])
          addBar(`dt|${start}|${end}`, {
            start,
            end,
            type: 'downtime',
            anchor: downtimeAnchor(row.line, 'line'),
          })
      }

      return Array.from(groups.values())
        .sort(
          (a, b) =>
            a.kitchen.localeCompare(b.kitchen) ||
            a.line.localeCompare(b.line) ||
            a.productCode.localeCompare(b.productCode)
        )
        .map(
          ({
            productCode,
            productDescription,
            line,
            kitchen,
            bars,
            prodH,
            cleanH,
            qty,
          }) => ({
            name: productLabel(productCode, productDescription),
            id: `${productCode}||${line}||${kitchen}`,
            description: `${line} ← ${kitchen}`,
            bars: bars.slice().sort((a, b) => a.start - b.start),
            util: Math.round((prodH / TOTAL_HOURS) * 100),
            prodH,
            cleanH,
            coCount: 0,
            qty,
          })
        )
    }, [filteredOutputRows, downtimeData])

    // Cleaning resources (CIP units) as their own timeline — one row per CIP
    // name, bars for every clean/pre-clean event it handles across all
    // kitchens and lines. Bars use the standard clean color (no per-kitchen
    // coloring here) with the asset name as the label.
    // One row per (cleaner, resource) pair — the same shape as the output
    // route's cleaning grid (`SchedulerView.CleaningView.tsx`, whose rows come
    // out of `GenerateCleaningRows.ts` nested CIP -> resources -> hours, with
    // Cleaner | Resource | Location columns). This view used to key by cleaner
    // alone, merging every kitchen and line a CIP served into one track, so a
    // given line's cleans could not be read off as their own row — the
    // resource only appeared in the bar label and tooltip.
    //
    // Both readings are offered: each cleaner gets a header row carrying every
    // clean it performs across all its resources on one track (so the cleaner's
    // own occupancy can be read off), with its (cleaner, resource) rows nested
    // underneath and collapsible, the same way By kitchen nests lines.
    const cleaningGroups = useMemo((): {
      cleaner: TLRowData
      resources: TLRowData[]
    }[] => {
      type CipEntry = {
        cip: string
        resource: string
        location?: string
        bars: TLBarData[]
        seen: Set<string>
        cleanH: number
      }
      // Keyed by cleaner + resource. `||` matches the separator the other
      // composite keys in this file use and cannot appear in either name.
      const cips = new Map<string, CipEntry>()

      const addCipBar = (
        side: CleanRef['side'],
        cipName: string | undefined,
        asset: string,
        start: number | null | undefined,
        end: number | null | undefined,
        reason: string | undefined,
        location?: string
      ) => {
        if (!cipName || start == null || end == null || end <= start) return
        const isMaxRun = reason === 'max_run_hours'
        const isPre = isMaxRun || reason === 'idle_preclean'
        const key = `${cipName}||${asset}`
        if (!cips.has(key))
          cips.set(key, {
            cip: cipName,
            resource: asset,
            location,
            bars: [],
            seen: new Set(),
            cleanH: 0,
          })
        const c = cips.get(key)!
        // A later row may be the one that carries the location.
        if (!c.location && location) c.location = location
        const barKey = `${asset}|${start}|${end}|${isPre ? 'pre' : 'c'}`
        if (c.seen.has(barKey)) return
        c.seen.add(barKey)
        c.bars.push({
          start,
          end,
          // `kind` is the pair this clean reads as; the strip matches on the
          // window, so an event that was promoted into the other pair is
          // still cleared correctly (see RemoveClean.ts).
          clean: {
            side,
            resource: asset,
            kind: isPre ? 'preclean' : 'clean',
            start,
            end,
          },
          type: isMaxRun ? 'maxrunclean' : isPre ? 'preclean' : 'clean',
          product: asset,
          productDescription: isMaxRun
            ? `Max-run clean · ${asset}`
            : isPre
              ? `Pre-clean · ${asset}`
              : `Clean · ${asset}`,
        })
        c.cleanH += end - start
      }

      for (const row of filteredOutputRows) {
        if (row.kitchen) {
          addCipBar(
            'kitchen',
            row.kitchenCIP,
            row.kitchen,
            row.kitchen_clean_start_time,
            row.kitchen_clean_end_time,
            cipReasonFor(
              row.additional_kitchen_cips,
              row.kitchen_clean_start_time,
              row.kitchen_clean_end_time
            ),
            row.site_location
          )
          addCipBar(
            'kitchen',
            row.kitchenPrecleanCIP,
            row.kitchen,
            row.kitchen_preclean_start_time,
            row.kitchen_preclean_end_time,
            cipReasonFor(
              row.additional_kitchen_cips,
              row.kitchen_preclean_start_time,
              row.kitchen_preclean_end_time
            ),
            row.site_location
          )
          for (const evt of parseAdditionalCips(row.additional_kitchen_cips))
            addCipBar(
              'kitchen',
              evt.cip,
              row.kitchen,
              evt.start,
              evt.end,
              evt.reason,
              row.site_location
            )
        }
        if (row.line) {
          addCipBar(
            'line',
            row.lineCIP,
            row.line,
            row.line_clean_start_time,
            row.line_clean_end_time,
            cipReasonFor(
              row.additional_line_cips,
              row.line_clean_start_time,
              row.line_clean_end_time
            ),
            row.site_location
          )
          addCipBar(
            'line',
            row.linePrecleanCIP,
            row.line,
            row.line_preclean_start_time,
            row.line_preclean_end_time,
            cipReasonFor(
              row.additional_line_cips,
              row.line_preclean_start_time,
              row.line_preclean_end_time
            ),
            row.site_location
          )
          for (const evt of parseAdditionalCips(row.additional_line_cips))
            addCipBar(
              'line',
              evt.cip,
              row.line,
              evt.start,
              evt.end,
              evt.reason,
              row.site_location
            )
        }
      }

      // Sorted cleaner first, then resource — the reading order of the
      // output route's Cleaner | Resource columns.
      const byCleaner = new Map<string, CipEntry[]>()
      for (const c of cips.values()) {
        if (!byCleaner.has(c.cip)) byCleaner.set(c.cip, [])
        byCleaner.get(c.cip)!.push(c)
      }
      return Array.from(byCleaner.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([cip, entries]) => {
          entries.sort((a, b) => a.resource.localeCompare(b.resource))
          const resources: TLRowData[] = entries.map(
            ({ resource, location, bars, cleanH }) => ({
              name: resource,
              description: location || undefined,
              bars: bars.slice().sort((a, b) => a.start - b.start),
              util: Math.round((cleanH / TOTAL_HOURS) * 100),
              prodH: 0,
              cleanH,
              coCount: 0,
              qty: 0,
            })
          )
          const bars = resources
            .flatMap((r) => r.bars)
            .sort((a, b) => a.start - b.start)
          // Utilisation is the cleaner's OCCUPANCY — the union of its clean
          // windows — not the sum of clean hours: two resources cleaned in
          // parallel by one CIP set would otherwise count the overlap twice
          // and could read above 100%.
          let busyH = 0
          let curS = -Infinity
          let curE = -Infinity
          for (const b of bars) {
            if (b.start > curE) {
              if (curE > curS) busyH += curE - curS
              curS = b.start
              curE = b.end
            } else if (b.end > curE) curE = b.end
          }
          if (curE > curS) busyH += curE - curS
          const cleanH = resources.reduce((s, r) => s + r.cleanH, 0)
          const cleaner: TLRowData = {
            name: cip,
            description: `${resources.length} resource${
              resources.length === 1 ? '' : 's'
            } · ${Math.round(cleanH * 10) / 10}h cleaning`,
            bars,
            util: Math.round((busyH / TOTAL_HOURS) * 100),
            prodH: 0,
            cleanH,
            coCount: 0,
            qty: 0,
          }
          return { cleaner, resources }
        })
    }, [filteredOutputRows])

    // Line grouping, expanded: one sub-row per product run on the line, the
    // way By kitchen expands a kitchen into its lines. Built from the line
    // row's own production bars, so it shows exactly what the line row draws
    // (tank drains included); cleans, changeovers and downtime stay on the
    // line row itself, since they belong to the line rather than a product.
    // Products are listed in the order the line first runs them.
    const lineProducts = useMemo(() => {
      const m = new Map<string, TLRowData[]>()
      for (const l of flatLines) {
        const byCode = new Map<string, { desc: string; bars: TLBarData[] }>()
        for (const b of l.bars) {
          if (b.type !== 'production') continue
          const code = b.product ?? ''
          if (!byCode.has(code))
            byCode.set(code, { desc: b.productDescription ?? '', bars: [] })
          const e = byCode.get(code)!
          if (!e.desc && b.productDescription) e.desc = b.productDescription
          e.bars.push(b)
        }
        m.set(
          l.name,
          Array.from(byCode, ([code, { desc, bars }]) => {
            const prodH = bars.reduce((s, b) => s + (b.end - b.start), 0)
            return {
              name: productLabel(code, desc),
              id: code,
              description: `${bars.length} run${bars.length === 1 ? '' : 's'}`,
              bars,
              util: Math.round((prodH / TOTAL_HOURS) * 100),
              prodH,
              cleanH: 0,
              coCount: 0,
              qty: bars.reduce((s, b) => s + (b.qty ?? 0), 0),
            }
          }).sort(
            (a, b) =>
              (a.bars[0]?.start ?? 0) - (b.bars[0]?.start ?? 0) ||
              a.name.localeCompare(b.name)
          )
        )
      }
      return m
    }, [flatLines])

    // ── Row order (sort / custom order), per grouping ────────────────────
    // Applies to the TOP-LEVEL rows only; nested rows keep their own order.
    const {
      mode: sortMode,
      order: customOrder,
      setMode: setSortMode,
      saveCustom: saveCustomOrder,
    } = useFactoryRowOrder(grouping)
    // The current grouping's top-level rows in their default order — what
    // the custom-order dialog lists.
    const orderableRows = useMemo((): OrderableRow[] => {
      const top: TLRowData[] =
        grouping === 'kitchen'
          ? groups.map((g) => g.kitchen)
          : grouping === 'combined'
            ? combinedRows
            : grouping === 'cleaning'
              ? cleaningGroups.map((g) => g.cleaner)
              : flatLines
      return top.map((r) => ({
        id: tlRowId(r),
        label: r.name,
        detail: grouping === 'combined' ? r.description : undefined,
      }))
    }, [grouping, groups, combinedRows, cleaningGroups, flatLines])

    const rows = useMemo(() => {
      type RowEntry = {
        kind: 'header' | 'line'
        row: TLRowData
        rollUtil?: number
        key: string
        /** Key into `collapsed` for a header. Namespaced so a cleaner and a
         *  kitchen that happen to share a name do not share a toggle. */
        collapseKey?: string
        /** Icon for a nested row: a line, or a product under a line. */
        rowIcon?: 'line' | 'product'
      }
      const order = <T,>(items: T[], row: (t: T) => TLRowData): T[] =>
        applyRowOrder(
          items,
          (t) => tlRowId(row(t)),
          (t) => row(t).name,
          sortMode,
          customOrder
        )
      const list: RowEntry[] = []
      if (grouping === 'kitchen') {
        order(groups, (g) => g.kitchen).forEach((g) => {
          list.push({
            kind: 'header',
            row: g.kitchen,
            rollUtil: g.rollUtil,
            key: `h-${g.kitchen.name}`,
            collapseKey: g.kitchen.name,
          })
          if (!collapsed[g.kitchen.name])
            g.lines.forEach((l) =>
              list.push({
                kind: 'line',
                row: l,
                key: `l-${g.kitchen.name}-${l.name}`,
              })
            )
        })
      } else if (grouping === 'combined') {
        order(combinedRows, (r) => r).forEach((r, i) =>
          list.push({
            kind: 'line',
            row: r,
            key: r.id ? `c-${r.id}` : `c-${i}-${r.name}`,
          })
        )
      } else if (grouping === 'cleaning') {
        order(cleaningGroups, (g) => g.cleaner).forEach((g) => {
          const collapseKey = `cln|${g.cleaner.name}`
          list.push({
            kind: 'header',
            row: g.cleaner,
            key: `clnh-${g.cleaner.name}`,
            collapseKey,
          })
          if (!collapsed[collapseKey])
            g.resources.forEach((r) =>
              list.push({
                kind: 'line',
                row: r,
                key: `cln-${g.cleaner.name}-${r.name}`,
              })
            )
        })
      } else {
        // Expanded by default, like a kitchen: `collapsed` holds only what
        // the planner has closed.
        order(flatLines, (l) => l).forEach((l) => {
          const collapseKey = `ln|${l.name}`
          list.push({
            kind: 'header',
            row: l,
            key: `f-${l.name}`,
            collapseKey,
          })
          if (!collapsed[collapseKey])
            (lineProducts.get(l.name) ?? []).forEach((p) =>
              list.push({
                kind: 'line',
                row: p,
                key: `fp-${l.name}-${p.id}`,
                rowIcon: 'product',
              })
            )
        })
      }
      return list
    }, [
      groups,
      flatLines,
      lineProducts,
      combinedRows,
      cleaningGroups,
      grouping,
      collapsed,
      sortMode,
      customOrder,
    ])

    // ── Manual clean removal ──────────────────────────────────────────
    // Read-only on an alternative answer, for the same reason cell edits are:
    // the write merges by row id against the iteration's OWN rows, so an edit
    // made while viewing "fastest" would land on the balanced schedule.
    const cleanEditable = scheduleVariant === 'balanced'
    const [pendingClean, setPendingClean] = useState<CleanRef | null>(null)
    const [removing, setRemoving] = useState(false)

    // ── Batch comments ────────────────────────────────────────────────────
    // One query per week for the whole timeline; each bar reads its own out of
    // the index. Read-only on an alternative answer for the same reason clean
    // removal is: a new comment would be anchored to a batch that only exists
    // in the answer being previewed.
    const commentWeek = selectedWeek ?? 1
    const commentsAvailable = Boolean(scheduleId && iteration)
    const { byAnchor: commentIndex } = useCommentIndex(
      scheduleId,
      iteration,
      commentWeek
    )
    const currentUserEmail = useCurrentUserEmail()
    const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(
      null
    )
    // The production run the open pop-up is for, so its "Explain" action
    // knows what to select. Separate from `commentTarget`, which is a comment
    // anchor and deliberately carries no stage.
    const [clickedRun, setClickedRun] = useState<MDRunFocus | null>(null)
    const [commentPosition, setCommentPosition] = useState<{
      top: number
      left: number
    } | null>(null)
    const closeComments = useCallback(() => {
      setCommentTarget(null)
      setCommentPosition(null)
    }, [])
    // Read live out of the index so posting or deleting redraws the open thread.
    const openThread = useMemo(
      () =>
        commentsFor(
          commentIndex,
          commentTarget ?? undefined,
          commentWeek,
          commentTarget ?? undefined
        ),
      [commentIndex, commentTarget, commentWeek]
    )
    const hoverComments = useMemo(
      () =>
        commentsFor(
          commentIndex,
          hoverTip?.b.anchor,
          commentWeek,
          hoverTip?.b
            ? { startTime: hoverTip.b.start, endTime: hoverTip.b.end }
            : undefined
        ),
      [commentIndex, hoverTip, commentWeek]
    )

    const { mutate: writeRows } = useUpdateScheduleVersionTableData(
      () => {
        setRemoving(false)
        setPendingClean(null)
        toast.success('Clean removed')
      },
      () => {
        setRemoving(false)
        toast.error('Could not remove the clean')
      }
    )

    // Rows are matched against the ABSOLUTE schedule, not the week-rebased
    // copy: the patch has to carry the times the stored rows actually hold, or
    // the Delta merge would write week-relative hours back over absolute ones.
    const resolvedClean = useMemo(
      () =>
        pendingClean
          ? resolveCleanRef(absoluteRows, pendingClean, weekBaseHour)
          : null,
      [pendingClean, absoluteRows, weekBaseHour]
    )
    const pendingRows = useMemo(
      () =>
        resolvedClean ? stripCleanFromRows(absoluteRows, resolvedClean) : {},
      [resolvedClean, absoluteRows]
    )
    const pendingReason = useMemo(
      () =>
        resolvedClean ? cleanReason(absoluteRows, resolvedClean) : undefined,
      [resolvedClean, absoluteRows]
    )

    // A clean bar opens the remove-clean dialog, a production bar opens its
    // comment thread — the two never collide, since no bar is both. Comments
    // stay reachable on an alternative answer (read-only there), which is why
    // this is no longer gated on `cleanEditable` at the call site.
    const onBarClick = useCallback(
      (b: TLBarData, e: React.MouseEvent) => {
        if (b.clean) {
          if (cleanEditable) setPendingClean(b.clean)
          return
        }
        // Selecting for the Model description does not depend on comments
        // being available, so it happens before that early return.
        const focus = runFocusFor(b, weekBaseHour)
        setClickedRun(focus)
        if (focus) onSelectRun?.(focus)
        if (!b.anchor || !commentsAvailable) return
        // A downtime thread now carries this bar's window. Storage is still
        // per resource — a window has no id — but recording the hours is what
        // lets `commentsFor` show the comment back on the window it was
        // written on rather than on all of them.
        const isDowntimeThread = b.anchor.kind === 'downtime'
        setCommentTarget({
          ...b.anchor,
          startTime: b.start,
          endTime: b.end,
          productDescription: isDowntimeThread
            ? `${b.anchor.resourceName} downtime`
            : b.productDescription,
        })
        setCommentPosition({ top: e.clientY, left: e.clientX })
      },
      [cleanEditable, commentsAvailable, weekBaseHour, onSelectRun]
    )

    const confirmRemoveClean = useCallback(() => {
      if (!pendingClean || !scheduleId) return
      const patches = pendingRows
      if (Object.keys(patches).length === 0) {
        toast.error('That clean is no longer in this schedule')
        setPendingClean(null)
        return
      }
      setRemoving(true)
      writeRows({
        scheduleId,
        iteration: iteration ?? '0',
        data: patches,
        week: selectedWeek,
      })
    }, [
      pendingClean,
      pendingRows,
      scheduleId,
      iteration,
      selectedWeek,
      writeRows,
    ])

    const onBarEnter = useCallback((b: TLBarData, e: React.MouseEvent) => {
      setHoverTip({ b, x: e.clientX, y: e.clientY })
      if (b.type === 'production')
        setHoverBase(b.product?.split('-')[0] ?? null)
    }, [])
    const onBarLeave = useCallback(() => {
      setHoverTip(null)
      setHoverBase(null)
    }, [])

    // Unmet demand — requires productionRequirements + outputRows
    const unmetDemand = useMemo(() => {
      if (!productionRequirements?.length) return null
      const scheduledMap = new Map<string, number>()
      outputRows.forEach((r) => {
        if (!r.productCode) return
        const qty =
          typeof r.quantity === 'string'
            ? parseFloat(r.quantity as unknown as string) || 0
            : (r.quantity ?? 0)
        const key = String(r.productCode)
        scheduledMap.set(key, (scheduledMap.get(key) ?? 0) + qty)
      })
      let total = 0
      productionRequirements.forEach((req) => {
        // Mirror Rust parse_product_requirements: quantityStillToMake overrides
        // quantity; stm=0 means already fully produced (effective demand = 0).
        const baseQty = parseFloat(String(req.quantity ?? 0)) || 0
        if (baseQty <= 0) return
        const stmRaw = (req as Record<string, unknown>).quantityStillToMake
        const stm = stmRaw != null ? parseFloat(String(stmRaw)) : null
        let demand: number
        if (stm !== null) {
          if (stm <= 0) return // already fully produced — not unmet
          demand = stm
        } else {
          const made =
            parseFloat(
              String((req as Record<string, unknown>).quantityMade ?? 0)
            ) || 0
          demand = Math.max(0, baseQty - made)
        }
        total += Math.max(
          0,
          demand - (scheduledMap.get(String(req.productCode)) ?? 0)
        )
      })
      return total
    }, [productionRequirements, outputRows])

    // Capacity breaches — total over-capacity hours across all Active crew groups.
    // Withheld while the Crew & capacity tab is hidden: it is that tab's figure,
    // from the same calculation, and a stat nobody can drill into is noise.
    const capacityBreaches = useMemo(() => {
      if (isTabHidden('crew') || !syncGroups?.length) return null
      return deriveCrewGroups(syncGroups, outputRows).reduce(
        (s, g) => s + g.overHours,
        0
      )
    }, [syncGroups, outputRows])

    const dfShort = (n: number) =>
      n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n))

    const utilTone = avgUtil >= 70 ? TOKENS.stateComplete : TOKENS.stateRunning
    const rowH = 27
    const headerH = rowH + 6

    // Mid-week reschedule progress — the Factory tab has no other run
    // visibility of its own, so this mirrors the pulsing banner built for
    // SchedulerInput.topStrip.tsx.
    const midWeekPct =
      midWeekProgress?.generation !== undefined &&
      midWeekProgress?.total_generations
        ? Math.min(
            100,
            (midWeekProgress.generation / midWeekProgress.total_generations) *
              100
          )
        : null
    const midWeekColor =
      midWeekProgress?.stage === 'failed'
        ? TOKENS.stateFailed
        : midWeekProgress?.stage === 'completed'
          ? TOKENS.stateComplete
          : TOKENS.stateRunning

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          bgcolor: TOKENS.surface0,
          ...T,
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1300,
          }),
        }}
        onMouseMove={(e: React.MouseEvent) =>
          hoverTip &&
          setHoverTip((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))
        }
      >
        {/* Header */}
        <Box
          sx={{ flexShrink: 0, borderBottom: `1px solid ${TOKENS.line200}` }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              px: '16px',
              py: '10px',
            }}
          >
            <ViewTimelineIcon
              sx={{ fontSize: 18, color: TOKENS.stateComplete }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.15,
              }}
            >
              <Box sx={{ ...TYPE.h2 }}>Factory schedule</Box>
              {iteration && (
                <Box
                  sx={{ fontSize: 10, color: TOKENS.ink400, fontWeight: 600 }}
                >
                  Iteration {iteration}
                  {/* What is ON SCREEN — the server's answer, not the
                      request's. Asking for the latest schedule on an
                      iteration that was never rescheduled returns the
                      original, and saying "Rescheduled" there would be a
                      lie the planner has no way to check. */}
                  {outputVariant && (
                    <Box
                      component="span"
                      sx={{
                        ml: '5px',
                        color:
                          outputVariant === 'latest' && hasReschedule
                            ? TOKENS.stateRunning
                            : TOKENS.ink400,
                      }}
                    >
                      ·{' '}
                      {outputVariant === 'latest' && hasReschedule
                        ? 'Rescheduled'
                        : 'Original'}
                    </Box>
                  )}
                  {/* Offered only once a reschedule exists to switch to —
                      before that there are not two things to choose between,
                      and a dead toggle reads as a broken one. */}
                  {onOutputVariantChange && hasReschedule && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() =>
                        onOutputVariantChange(
                          outputVariant === 'latest' ? 'original' : 'latest'
                        )
                      }
                      sx={{
                        ml: '6px',
                        px: '5px',
                        py: '1px',
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        color: TOKENS.brandBlue,
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: TOKENS.line300,
                        borderRadius: '2px',
                        cursor: 'pointer',
                        '&:hover': { borderColor: TOKENS.brandBlue },
                      }}
                    >
                      {outputVariant === 'latest'
                        ? 'Show original'
                        : 'Show rescheduled'}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1 }} />
            {/* Metrics */}
            {[
              { label: 'Makespan', value: makespan.toFixed(0), unit: 'h' },
              {
                label: 'Production hours',
                value: totalProdHours.toFixed(1),
                unit: 'h',
              },
              {
                label: 'Total quantity',
                value: dfShort(totalQuantity),
              },
              { label: 'Products', value: String(productCount) },
              { label: 'Lines', value: String(lineCount) },
              { label: 'Kitchens', value: String(kitchenCount) },
              {
                label: 'Avg util',
                value: String(avgUtil),
                unit: '%',
                tone: utilTone,
              },
              { label: 'Changeovers', value: String(changeovers) },
              { label: 'CIP', value: totalCleanH.toFixed(0), unit: 'h' },
              ...(unmetDemand !== null
                ? [
                    {
                      label: 'Unmet',
                      value: dfShort(unmetDemand),
                      tone:
                        unmetDemand > 0
                          ? TOKENS.ganttDowntime
                          : TOKENS.stateComplete,
                    },
                  ]
                : []),
              ...(capacityBreaches !== null
                ? [
                    {
                      label: 'Breaches',
                      value: String(capacityBreaches),
                      tone:
                        capacityBreaches > 0
                          ? TOKENS.ganttDowntime
                          : TOKENS.stateComplete,
                    },
                  ]
                : []),
            ].map((m, i, arr) => (
              <Box
                key={m.label}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                  pr: i < arr.length - 1 ? '18px' : 0,
                  borderRight:
                    i < arr.length - 1 ? `1px solid ${TOKENS.line200}` : 'none',
                  mr: i < arr.length - 1 ? '18px' : 0,
                }}
              >
                <Box
                  sx={{
                    fontSize: 9,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.07em',
                    color: TOKENS.ink400,
                  }}
                >
                  {m.label}
                </Box>
                <Box
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 17,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: (m as { tone?: string }).tone ?? TOKENS.ink900,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {m.value}
                  {m.unit && (
                    <Box
                      component="span"
                      sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                    >
                      {m.unit}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          {/* Mid-week reschedule progress */}
          {midWeekRunning && midWeekProgress && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                px: '16px',
                py: '8px',
                borderTop: `1px solid ${TOKENS.line100}`,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: midWeekColor,
                  flexShrink: 0,
                  '@keyframes factoryMidWeekPulse': {
                    '0%': { boxShadow: `0 0 0 0 ${midWeekColor}66` },
                    '70%': { boxShadow: `0 0 0 7px ${midWeekColor}00` },
                    '100%': { boxShadow: `0 0 0 0 ${midWeekColor}00` },
                  },
                  animation: 'factoryMidWeekPulse 1.6s ease-out infinite',
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    mb: '3px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '10px',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: TOKENS.ink700,
                        fontFamily: FONT_SANS,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {midWeekProgress.message}
                    </Box>
                    {midWeekPct !== null && (
                      <Box
                        sx={{
                          fontFamily: FONT_MONO,
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: midWeekColor,
                          flexShrink: 0,
                        }}
                      >
                        Generation {midWeekProgress.generation}/
                        {midWeekProgress.total_generations}
                        <Box
                          component="span"
                          sx={{ color: TOKENS.ink400, ml: '6px' }}
                        >
                          ({midWeekPct.toFixed(0)}%)
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {midWeekProgress.elapsed_ms !== undefined && (
                    <Box
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 10.5,
                        color: TOKENS.ink400,
                        flexShrink: 0,
                        ml: '10px',
                      }}
                    >
                      {(midWeekProgress.elapsed_ms / 1000).toFixed(1)}s elapsed
                    </Box>
                  )}
                </Box>
                <LinearProgress
                  variant={
                    midWeekPct !== null ? 'determinate' : 'indeterminate'
                  }
                  value={midWeekPct ?? undefined}
                  sx={{
                    height: 7,
                    borderRadius: 4,
                    bgcolor: TOKENS.line200,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: midWeekColor,
                    },
                  }}
                />
              </Box>
            </Box>
          )}
          {/* Legend strip */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '14px',
              rowGap: '8px',
              px: '16px',
              py: '7px',
              bgcolor: TOKENS.surface50,
              borderTop: `1px solid ${TOKENS.line100}`,
            }}
          >
            <TLLegend
              showReschedule={outputRows.some((r) => r.fixed === true)}
            />
            <Box sx={{ flex: 1 }} />
            {/* Filters — centered between the legend and the grouping/view
                controls, narrows every grouping mode (and its KPIs) to rows
                matching kitchen, line, product, base material or CIP
                identifiers. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexShrink: 0,
              }}
            >
              <TLSearchBox
                value={search}
                onChange={setSearch}
                placeholder="Filter product code or name, kitchen, line…"
                width={230}
              />
              {/* Kitchen / Line / Product / Base material / Location selectors */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TLFilterSelect
                  label="Kitchen"
                  value={kitchenFilter}
                  onChange={setKitchenFilter}
                  options={filterOptions.kitchens}
                />
                <TLFilterSelect
                  label="Line"
                  value={lineFilter}
                  onChange={setLineFilter}
                  options={filterOptions.lines}
                />
                <TLFilterSelect
                  label="Product"
                  value={productFilter}
                  onChange={setProductFilter}
                  options={filterOptions.products}
                />
                <TLFilterSelect
                  label="Base material"
                  value={baseMaterialFilter}
                  onChange={setBaseMaterialFilter}
                  options={filterOptions.baseMaterials}
                />
                {filterOptions.locations.length > 0 && (
                  <TLFilterSelect
                    label="Location"
                    value={locationFilter}
                    onChange={setLocationFilter}
                    options={filterOptions.locations}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            {/* Row order — per grouping, remembered in this browser */}
            <FactoryRowOrderControl
              mode={sortMode}
              onModeChange={setSortMode}
              rows={orderableRows}
              customOrder={customOrder}
              onSaveCustom={saveCustomOrder}
              rowNoun={
                grouping === 'kitchen'
                  ? 'kitchens'
                  : grouping === 'flat'
                    ? 'lines'
                    : grouping === 'combined'
                      ? 'product rows'
                      : 'cleaners'
              }
            />
            {/* Grouping */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {(['combined', 'kitchen', 'flat', 'cleaning'] as const).map(
                (g) => (
                  <Box
                    key={g}
                    component="button"
                    onClick={() => setGrouping(g)}
                    sx={{
                      border: `1px solid ${grouping === g ? TOKENS.stateComplete : TOKENS.line300}`,
                      borderRadius: '2px',
                      px: '8px',
                      height: 24,
                      cursor: 'pointer',
                      bgcolor:
                        grouping === g ? 'rgba(14,124,134,0.08)' : '#fff',
                      fontSize: 10.5,
                      fontWeight: 600,
                      color:
                        grouping === g ? TOKENS.stateComplete : TOKENS.ink500,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    {g === 'kitchen'
                      ? 'By kitchen'
                      : g === 'flat'
                        ? 'Line'
                        : g === 'combined'
                          ? 'Combined'
                          : 'Cleaning'}
                  </Box>
                )
              )}
            </Box>
            {/* Optional slot: e.g. view toggle injected by parent */}
            {headerRight}
            {/* Download + fullscreen */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                ml: '8px',
                pl: '12px',
                borderLeft: `1px solid ${TOKENS.line200}`,
              }}
            >
              <Tooltip title="Download schedule (CSV)">
                <Box
                  component="button"
                  onClick={() =>
                    downloadScheduleCsv(
                      outputRows,
                      selectedWeek,
                      scheduleVariant
                    )
                  }
                  disabled={!outputRows.length}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${TOKENS.line300}`,
                    borderRadius: '2px',
                    width: 24,
                    height: 24,
                    p: 0,
                    cursor: outputRows.length ? 'pointer' : 'not-allowed',
                    opacity: outputRows.length ? 1 : 0.4,
                    bgcolor: '#fff',
                    color: TOKENS.ink500,
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 15 }} />
                </Box>
              </Tooltip>
              <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                <Box
                  component="button"
                  onClick={() => setIsFullscreen((v) => !v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${isFullscreen ? TOKENS.stateComplete : TOKENS.line300}`,
                    borderRadius: '2px',
                    width: 24,
                    height: 24,
                    p: 0,
                    cursor: 'pointer',
                    bgcolor: isFullscreen ? 'rgba(14,124,134,0.08)' : '#fff',
                    color: isFullscreen ? TOKENS.stateComplete : TOKENS.ink500,
                  }}
                >
                  {isFullscreen ? (
                    <FullscreenExitIcon sx={{ fontSize: 15 }} />
                  ) : (
                    <FullscreenIcon sx={{ fontSize: 15 }} />
                  )}
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Scrollable timeline body */}
        <Box
          sx={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}
        >
          <Box
            sx={{
              position: 'relative',
              width: fit ? 'auto' : RAIL + trackW,
              minWidth: fit ? 860 : RAIL + trackW,
            }}
          >
            <TLAxis trackW={trackW} fit={fit} />
            {rows.length === 0 ? (
              <Box
                sx={{
                  p: '40px 20px',
                  textAlign: 'center',
                  color: TOKENS.ink400,
                  fontSize: 12,
                  ...T,
                }}
              >
                {outputRows.length === 0
                  ? 'No schedule output yet — run the scheduler to populate the timeline.'
                  : 'No rows match your filter.'}
              </Box>
            ) : (
              rows.map((r) => (
                <TLRow
                  key={r.key}
                  row={r.row}
                  isHeader={r.kind === 'header'}
                  rollUtil={r.rollUtil}
                  collapsed={
                    r.collapseKey != null
                      ? (collapsed[r.collapseKey] ?? false)
                      : false
                  }
                  onToggle={
                    r.collapseKey != null
                      ? () =>
                          setCollapsed((c) => ({
                            ...c,
                            [r.collapseKey!]: !c[r.collapseKey!],
                          }))
                      : undefined
                  }
                  headerIcon={
                    grouping === 'cleaning'
                      ? 'cleaner'
                      : grouping === 'flat'
                        ? 'line'
                        : 'kitchen'
                  }
                  rowIcon={r.rowIcon}
                  rowH={r.kind === 'header' ? headerH : rowH}
                  hoverBase={hoverBase}
                  trackW={trackW}
                  fit={fit}
                  onEnter={onBarEnter}
                  onLeave={onBarLeave}
                  onBarClick={onBarClick}
                  selectedRun={selectedRun}
                  cleanClickable={cleanEditable}
                  commentIndex={commentIndex}
                  commentWeek={commentWeek}
                />
              ))
            )}
            <Box sx={{ height: 12 }} />
          </Box>
        </Box>

        <TLTooltip data={hoverTip} comments={hoverComments} />

        {commentsAvailable && (
          <ScheduleCommentPopover
            schedulerId={scheduleId as string}
            iteration={iteration as string}
            weekNumber={commentWeek}
            target={commentTarget}
            position={commentPosition}
            comments={openThread}
            readOnly={!cleanEditable}
            readOnlyReason={`Viewing the ${scheduleVariant.replace('_', ' ')} answer — comments can only be added on the schedule's own answer.`}
            currentUserEmail={currentUserEmail}
            onClose={closeComments}
            action={
              onExplainRun && clickedRun
                ? {
                    label: 'Explain in Model description',
                    onClick: () => {
                      onExplainRun(clickedRun)
                      closeComments()
                    },
                  }
                : undefined
            }
          />
        )}

        <RemoveCleanDialog
          // The clicked bar's identity when resolution fails, so the dialog
          // still opens and can explain why nothing can be removed rather
          // than the click appearing to do nothing.
          ref_={resolvedClean ?? pendingClean}
          reason={pendingReason}
          rowCount={Object.keys(pendingRows).length}
          busy={removing}
          onCancel={() => setPendingClean(null)}
          onConfirm={confirmRemoveClean}
        />
      </Box>
    )
  }
)
TimelineTab.displayName = 'TimelineTab'

const BAR_COLOR: Record<string, string> = {
  production: TOKENS.ganttProduction,
  clean: TOKENS.ganttClean,
  preclean: TOKENS.ganttPreClean,
  maxrunclean: TOKENS.ganttMaxRunClean,
  changeover: TOKENS.ganttChangeover,
  downtime: TOKENS.ganttDowntime,
}

// ═══════════════════════════════════════════════════════════════════════════════
// CREW & CAPACITY TAB  (ports CrewCapacity.jsx)
// ═══════════════════════════════════════════════════════════════════════════════

type CrewGroupData = {
  id: number
  name: string
  lines: string[]
  maxActive: number
  loadBands: { start: number; end: number; count: number }[]
  peak: number
  overHours: number
}

function deriveCrewGroups(
  syncGroups: SynchronizedLinesRowType[],
  outputRows: InputRow[]
): CrewGroupData[] {
  return syncGroups
    .filter((g) => String(g.productMasterType).toLowerCase() === 'active')
    .map((g) => {
      const lines = String(g.lines ?? '')
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
      const maxActive = Number(g.active) || 1
      const hourly = new Array(TOTAL_HOURS).fill(0)
      for (const row of outputRows) {
        if (!lines.includes(row.line)) continue
        const s = Math.max(0, Math.floor(row.line_start_time ?? 0)),
          e = Math.min(TOTAL_HOURS, Math.ceil(row.line_end_time ?? 0))
        for (let h = s; h < e; h++) hourly[h]++
      }
      const bands: { start: number; end: number; count: number }[] = []
      let cur: { start: number; end: number; count: number } | null = null
      for (let h = 0; h <= TOTAL_HOURS; h++) {
        const c = h < TOTAL_HOURS ? hourly[h] : -1
        if (cur === null) {
          if (c > 0) cur = { start: h, end: h + 1, count: c }
        } else if (c !== cur.count) {
          bands.push(cur)
          cur = c > 0 ? { start: h, end: h + 1, count: c } : null
        } else cur.end = h + 1
      }
      return {
        id: g.id,
        name: g.groupName,
        lines,
        maxActive,
        loadBands: bands,
        peak: Math.max(0, ...hourly),
        overHours: hourly.filter((v) => v > maxActive).length,
      }
    })
}

function getLineBarsForCrew(
  lineName: string,
  outputRows: InputRow[]
): { start: number; end: number; type: string }[] {
  const bars: { start: number; end: number; type: string }[] = []
  const seen = new Set<string>()
  for (const row of outputRows) {
    if (row.line !== lineName) continue
    const add = (
      s: number | null | undefined,
      e: number | null | undefined,
      type: string
    ) => {
      if (s == null || e == null) return
      const k = `${type}-${s}-${e}`
      if (!seen.has(k)) {
        seen.add(k)
        bars.push({ start: s, end: e, type })
      }
    }
    add(row.line_start_time, row.line_end_time, 'production')
    add(row.line_clean_start_time, row.line_clean_end_time, 'clean')
    add(row.line_preclean_start_time, row.line_preclean_end_time, 'preclean')
  }
  return bars
}

const CrewStat = memo(
  ({
    label,
    value,
    unit,
    tone,
  }: {
    label: string
    value: string
    unit?: string
    tone?: string
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <Box
        sx={{
          fontSize: 9,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: TOKENS.ink400,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 15,
          fontWeight: 600,
          color: tone ?? TOKENS.ink900,
          lineHeight: 1,
        }}
      >
        {value}
        {unit && (
          <Box
            component="span"
            sx={{ fontSize: 10, color: TOKENS.ink400, ml: '3px' }}
          >
            {unit}
          </Box>
        )}
      </Box>
    </Box>
  )
)
CrewStat.displayName = 'CrewStat'

const CrewCard = memo(
  ({ group, outputRows }: { group: CrewGroupData; outputRows: InputRow[] }) => {
    const tone =
      group.overHours > 0 ? TOKENS.ganttDowntime : TOKENS.stateComplete
    return (
      <Box
        sx={{
          border: `1px solid ${TOKENS.line200}`,
          borderRadius: '3px',
          bgcolor: '#fff',
          mb: '14px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '14px',
            py: '11px',
            borderBottom: `1px solid ${TOKENS.line100}`,
            bgcolor: TOKENS.surface50,
          }}
        >
          <GroupsIcon sx={{ fontSize: 17, color: TOKENS.ganttClean }} />
          <Box
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: TOKENS.ink900,
              flex: 1,
              fontFamily: FONT_SANS,
            }}
          >
            {group.name}
          </Box>
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>Max active</Box>
          <Box
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 14,
              fontWeight: 700,
              color: TOKENS.stateComplete,
            }}
          >
            {group.maxActive}
          </Box>
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>lines</Box>
        </Box>
        <Box sx={{ p: '12px 14px 14px' }}>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ width: 88, minWidth: 88 }} />
            <Box sx={{ flex: 1 }}>
              <WeekDayHeader />
            </Box>
          </Box>
          {group.lines.map((lineName) => {
            const bars = getLineBarsForCrew(lineName, outputRows)
            return (
              <Box
                key={lineName}
                sx={{ display: 'flex', alignItems: 'center', height: 24 }}
              >
                <Box
                  sx={{
                    width: 88,
                    minWidth: 88,
                    textAlign: 'right',
                    pr: '10px',
                    fontSize: 11,
                    fontFamily: FONT_MONO,
                    fontWeight: 500,
                    color: TOKENS.ink600,
                  }}
                >
                  {lineName}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <OWeekTrack height={15}>
                    {bars.map((b, i) => (
                      <OBand
                        key={i}
                        start={b.start}
                        end={b.end}
                        color={BAR_COLOR[b.type] ?? TOKENS.ganttChangeover}
                        radius={2}
                        top={2}
                        bottom={2}
                      />
                    ))}
                  </OWeekTrack>
                </Box>
              </Box>
            )
          })}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 30,
              mt: '6px',
              pt: '6px',
              borderTop: `1px solid ${TOKENS.line100}`,
            }}
          >
            <Box
              sx={{
                width: 88,
                minWidth: 88,
                textAlign: 'right',
                pr: '10px',
                fontSize: 11,
                fontFamily: FONT_MONO,
                fontWeight: 700,
                color: tone,
              }}
            >
              Load
            </Box>
            <Box sx={{ flex: 1 }}>
              <OWeekTrack height={20} bg={TOKENS.surface50}>
                {group.loadBands.map((b, i) => {
                  const over = b.count > group.maxActive,
                    atCap = b.count === group.maxActive
                  const color = over
                    ? TOKENS.ganttDowntime
                    : atCap
                      ? TOKENS.ganttClean
                      : TOKENS.ganttProduction
                  return (
                    <OBand
                      key={i}
                      start={b.start}
                      end={b.end}
                      color={color}
                      label={String(b.count)}
                      title={`${b.count} active line${b.count !== 1 ? 's' : ''}${over ? ' — over capacity' : ''}`}
                      radius={0}
                      top={0}
                      bottom={0}
                    />
                  )
                })}
              </OWeekTrack>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '18px',
              pl: '88px',
              mt: '9px',
            }}
          >
            <CrewStat
              label="Peak load"
              value={String(group.peak)}
              unit={`/ ${group.maxActive}`}
              tone={
                group.peak > group.maxActive
                  ? TOKENS.ganttDowntime
                  : TOKENS.ink900
              }
            />
            <CrewStat
              label="Over-cap hours"
              value={String(group.overHours)}
              unit="h"
              tone={
                group.overHours ? TOKENS.ganttDowntime : TOKENS.stateComplete
              }
            />
            <CrewStat label="Member lines" value={String(group.lines.length)} />
            <Box sx={{ flex: 1 }} />
            {group.overHours > 0 ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 11.5,
                  color: TOKENS.ganttDowntime,
                  fontWeight: 600,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 15 }} />
                {group.overHours}h above {group.maxActive}-line limit
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 11.5,
                  color: TOKENS.stateComplete,
                  fontWeight: 600,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 15 }} />
                Within crew capacity
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  }
)
CrewCard.displayName = 'CrewCard'

const CrewCapacityTab = memo(
  ({
    outputRows,
    syncGroups,
  }: {
    outputRows: InputRow[]
    syncGroups: SynchronizedLinesRowType[]
  }) => {
    const groups = useMemo(
      () => deriveCrewGroups(syncGroups, outputRows),
      [syncGroups, outputRows]
    )
    const totalOver = groups.reduce((s, c) => s + c.overHours, 0)
    const breached = groups.filter((c) => c.overHours > 0).length
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<GroupsIcon sx={{ fontSize: 19 }} />}
          title="Crew & capacity"
          sub="Concurrent line load against each crew's limit"
          metrics={[
            { label: 'Crews', value: groups.length },
            {
              label: 'Over-cap hours',
              value: totalOver,
              unit: 'h',
              tone: totalOver ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
            {
              label: 'Breached',
              value: `${breached}/${groups.length}`,
              tone: breached ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            px: '18px',
            py: '16px',
            pb: '24px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              mb: '12px',
              flexWrap: 'wrap',
            }}
          >
            {[
              [TOKENS.ganttProduction, 'production'],
              [TOKENS.ganttClean, 'at capacity / CIP'],
              [TOKENS.ganttProduction, 'load (within limit)'],
              [TOKENS.ganttDowntime, 'over capacity'],
            ].map(([c, l]) => (
              <Box
                key={l}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: 10.5,
                  color: TOKENS.ink500,
                }}
              >
                <Box
                  sx={{
                    width: 11,
                    height: 11,
                    borderRadius: '2px',
                    bgcolor: c,
                  }}
                />
                {l}
              </Box>
            ))}
          </Box>
          {groups.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No crew groups (Active type) defined in synchronized lines.
            </Box>
          ) : (
            groups.map((c) => (
              <CrewCard key={c.id} group={c} outputRows={outputRows} />
            ))
          )}
        </Box>
      </Box>
    )
  }
)
CrewCapacityTab.displayName = 'CrewCapacityTab'

// ═══════════════════════════════════════════════════════════════════════════════
// DEMAND FULFILMENT TAB  (ports DemandFulfil.jsx)
// ═══════════════════════════════════════════════════════════════════════════════

const dfK = (n: number) =>
  n >= 1000
    ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
    : String(Math.round(n))
type DemandRowData = {
  code: string
  name: string
  demand: number
  produced: number
  pct: number
  status: 'met' | 'short'
}

function deriveDemand(
  productionRequirements: ProductionRequirementRowType[],
  outputRows: InputRow[]
) {
  const scheduledMap = new Map<string, number>()
  for (const row of outputRows) {
    if (!row.productCode) continue
    const qty =
      typeof row.quantity === 'string'
        ? parseFloat(row.quantity as unknown as string) || 0
        : (row.quantity ?? 0)
    const key = String(row.productCode)
    scheduledMap.set(key, (scheduledMap.get(key) ?? 0) + qty)
  }
  const rows: DemandRowData[] = productionRequirements
    .map((r) => {
      const baseQty = Number(r.quantity ?? 0)
      if (baseQty <= 0) return null
      const stmRaw = (r as Record<string, unknown>).quantityStillToMake
      const stm = stmRaw != null ? Number(stmRaw) : null
      let demand: number
      if (stm !== null) {
        if (stm <= 0) return null // stm=0 → already produced, skip
        demand = stm
      } else {
        const made =
          Number((r as Record<string, unknown>).quantityMade ?? 0) || 0
        demand = Math.max(0, baseQty - made)
        if (demand <= 0) return null
      }
      const produced = Math.min(
        demand,
        scheduledMap.get(String(r.productCode)) ?? 0
      )
      return {
        code: r.productCode,
        name: r.productName ?? r.productCode,
        demand,
        produced,
        pct: demand > 0 ? produced / demand : 1,
        status: produced >= demand ? 'met' : 'short',
      } as DemandRowData
    })
    .filter((r): r is DemandRowData => r !== null)
    .sort((a, b) => a.pct - b.pct)
  const totalDemand = rows.reduce((s, r) => s + r.demand, 0),
    totalProduced = rows.reduce((s, r) => s + r.produced, 0)
  return {
    rows,
    summary: {
      demand: totalDemand,
      produced: totalProduced,
      pct:
        totalDemand > 0 ? Math.round((totalProduced / totalDemand) * 100) : 100,
      unmet: Math.max(0, totalDemand - totalProduced),
      met: rows.filter((r) => r.status === 'met').length,
      short: rows.filter((r) => r.status === 'short').length,
    },
  }
}

const DemandDataRow = memo(
  ({ row, maxDemand }: { row: DemandRowData; maxDemand: number }) => {
    const isShort = row.status === 'short',
      tone = isShort ? TOKENS.ganttDowntime : TOKENS.stateComplete
    const trackW = maxDemand > 0 ? (row.demand / maxDemand) * 100 : 0,
      unmet = row.demand - row.produced
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: 34,
          borderBottom: `1px solid ${TOKENS.line100}`,
        }}
      >
        <Box
          sx={{
            width: 168,
            minWidth: 168,
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: TOKENS.ink900,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name}
          </Box>
          <Box
            sx={{ fontSize: 9.5, color: TOKENS.ink300, fontFamily: FONT_MONO }}
          >
            {row.code}
          </Box>
        </Box>
        <Box
          sx={{ flex: 1, minWidth: 90, display: 'flex', alignItems: 'center' }}
        >
          <Tooltip
            title={`${dfK(row.produced)} of ${dfK(row.demand)} produced`}
            arrow
          >
            <Box
              sx={{
                width: `${trackW}%`,
                minWidth: 4,
                height: 14,
                display: 'flex',
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${TOKENS.line200}`,
              }}
            >
              <Box
                sx={{
                  width: `${row.pct * 100}%`,
                  bgcolor: TOKENS.ganttProduction,
                }}
              />
              {unmet > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    background:
                      'repeating-linear-gradient(45deg, rgba(196,69,54,0.22) 0 4px, rgba(196,69,54,0.40) 4px 8px)',
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
        <Box
          sx={{
            width: 50,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 13,
            fontWeight: 600,
            color: tone,
          }}
        >
          {Math.round(row.pct * 100)}%
        </Box>
        <Box
          sx={{
            width: 96,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: TOKENS.ink500,
          }}
        >
          {dfK(row.produced)} / {dfK(row.demand)}
        </Box>
        <Box
          sx={{
            width: 78,
            textAlign: 'right',
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: unmet > 0 ? TOKENS.ganttDowntime : TOKENS.ink300,
          }}
        >
          {unmet > 0 ? `−${dfK(unmet)}` : '—'}
        </Box>
        <Box sx={{ width: 74 }}>
          <SchChip tone={isShort ? 'failed' : 'complete'}>
            {isShort ? 'Short' : 'Met'}
          </SchChip>
        </Box>
      </Box>
    )
  }
)
DemandDataRow.displayName = 'DemandDataRow'

const DemandFulfilmentTab = memo(
  ({
    productionRequirements,
    outputRows,
  }: {
    productionRequirements: ProductionRequirementRowType[]
    outputRows: InputRow[]
  }) => {
    const { rows, summary } = useMemo(
      () => deriveDemand(productionRequirements, outputRows),
      [productionRequirements, outputRows]
    )
    const maxDemand = rows.length ? Math.max(...rows.map((r) => r.demand)) : 1
    const fulfilTone =
      summary.pct >= 98
        ? TOKENS.stateComplete
        : summary.pct >= 92
          ? TOKENS.stateRunning
          : TOKENS.ganttDowntime
    const STATUS_CARDS = [
      {
        key: 'met' as const,
        count: summary.met,
        label: 'Met',
        color: TOKENS.stateComplete,
        icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      },
      {
        key: 'short' as const,
        count: summary.short,
        label: 'Short',
        color: TOKENS.ganttDowntime,
        icon: <ErrorIcon sx={{ fontSize: 20 }} />,
      },
    ]
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<InventoryIcon sx={{ fontSize: 19 }} />}
          title="Demand fulfilment"
          sub="Scheduled production against required demand, by SKU"
          metrics={[
            {
              label: 'Fulfilment',
              value: summary.pct,
              unit: '%',
              tone: fulfilTone,
            },
            { label: 'Demand', value: dfK(summary.demand) },
            { label: 'Produced', value: dfK(summary.produced) },
            {
              label: 'Unmet',
              value: dfK(summary.unmet),
              tone: summary.unmet ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            px: '18px',
            py: '16px',
            pb: '24px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px', mb: '18px' }}>
            {STATUS_CARDS.map(({ key, count, label, color, icon }) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  p: '11px 14px',
                  border: `1px solid ${TOKENS.line200}`,
                  borderRadius: '3px',
                  bgcolor: '#fff',
                  borderLeft: `3px solid ${color}`,
                }}
              >
                <Box sx={{ color }}>{icon}</Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    lineHeight: 1.1,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: 22,
                      fontWeight: 700,
                      color: TOKENS.ink900,
                    }}
                  >
                    {count}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 10.5,
                      color: TOKENS.ink400,
                      fontWeight: 600,
                    }}
                  >
                    {label} {count === 1 ? 'SKU' : 'SKUs'}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 12,
                    color: TOKENS.ink300,
                  }}
                >
                  {rows.length > 0
                    ? `${Math.round((count / rows.length) * 100)}%`
                    : '—'}
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              height: 22,
            }}
          >
            {[
              { label: 'Product', w: 168 },
              { label: 'Produced vs demand', flex: true },
              { label: 'Fill', w: 50, align: 'right' as const },
              { label: 'Units', w: 96, align: 'right' as const },
              { label: 'Unmet', w: 78, align: 'right' as const },
            ].map((col) => (
              <Box
                key={col.label}
                sx={{
                  ...(col.flex
                    ? { flex: 1 }
                    : { width: col.w, minWidth: col.w }),
                  textAlign: (col as { align?: 'right' }).align ?? 'left',
                  fontSize: 8.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: TOKENS.ink300,
                }}
              >
                {col.label}
              </Box>
            ))}
            <Box sx={{ width: 74 }} />
          </Box>
          {rows.map((r) => (
            <DemandDataRow key={r.code} row={r} maxDemand={maxDemand} />
          ))}
          {rows.length === 0 && (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No production requirements data.
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)
DemandFulfilmentTab.displayName = 'DemandFulfilmentTab'

// ═══════════════════════════════════════════════════════════════════════════════
// SYNC GROUPS TAB  (ports SyncGroups.jsx)
// ═══════════════════════════════════════════════════════════════════════════════

const SYNC_COLORS = [
  TOKENS.ganttProduction,
  TOKENS.stateComplete,
  TOKENS.ganttClean,
  '#7a5fb0',
  TOKENS.ganttChangeover,
  TOKENS.ganttDowntime,
]
type SyncMember = {
  name: string
  runs: { start: number; end: number }[]
  coverage: number
}
type SyncGroupData = {
  id: number
  name: string
  color: string
  temp: string
  members: SyncMember[]
  adherence: number
  drift: string[]
  windows: [number, number][]
}

function deriveSyncGroups(
  syncGroups: SynchronizedLinesRowType[],
  outputRows: InputRow[]
): SyncGroupData[] {
  return syncGroups
    .filter((g) => String(g.productMasterType).toLowerCase() === 'temperature')
    .map((g, gi) => {
      const lines = String(g.lines ?? '')
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
      const color = SYNC_COLORS[gi % SYNC_COLORS.length]
      const temp = (g.groupName.match(/(\d+)/) ?? [])[1] ?? ''
      const lineRunsMap = new Map<string, { start: number; end: number }[]>()
      for (const lineName of lines) {
        const runs: { start: number; end: number }[] = []
        for (const row of outputRows) {
          if (
            row.line !== lineName ||
            row.line_start_time == null ||
            row.line_end_time == null
          )
            continue
          runs.push({ start: row.line_start_time, end: row.line_end_time })
        }
        lineRunsMap.set(lineName, runs)
      }
      const unionHours = new Set<number>()
      for (const [, runs] of lineRunsMap)
        for (const r of runs)
          for (
            let h = Math.floor(r.start);
            h < Math.ceil(r.end) && h < TOTAL_HOURS;
            h++
          )
            unionHours.add(h)
      const members: SyncMember[] = lines.map((lineName) => {
        const runs = lineRunsMap.get(lineName) ?? []
        const lineHours = new Set<number>()
        for (const r of runs)
          for (
            let h = Math.floor(r.start);
            h < Math.ceil(r.end) && h < TOTAL_HOURS;
            h++
          )
            lineHours.add(h)
        return {
          name: lineName,
          runs,
          coverage: unionHours.size > 0 ? lineHours.size / unionHours.size : 0,
        }
      })
      const sortedH = Array.from(unionHours).sort((a, b) => a - b)
      const windows: [number, number][] = []
      let ws = -1,
        we = -1
      for (const h of sortedH) {
        if (ws < 0) {
          ws = h
          we = h + 1
        } else if (h === we) {
          we++
        } else {
          windows.push([ws, we])
          ws = h
          we = h + 1
        }
      }
      if (ws >= 0) windows.push([ws, we])
      const adherence =
        members.length > 0
          ? members.reduce((s, m) => s + m.coverage, 0) / members.length
          : 0
      return {
        id: g.id,
        name: g.groupName,
        color,
        temp,
        members,
        adherence,
        drift: members.filter((m) => m.coverage < 0.62).map((m) => m.name),
        windows,
      }
    })
}

const SyncCard = memo(({ group }: { group: SyncGroupData }) => {
  const adh = Math.round(group.adherence * 100)
  const adhTone =
    adh >= 85
      ? TOKENS.stateComplete
      : adh >= 70
        ? TOKENS.stateRunning
        : TOKENS.ganttDowntime
  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '3px',
        bgcolor: '#fff',
        mb: '14px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '14px',
          py: '11px',
          borderBottom: `1px solid ${TOKENS.line100}`,
          bgcolor: TOKENS.surface50,
        }}
      >
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: '3px',
            bgcolor: group.color,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: TOKENS.ink900,
            flex: 1,
            fontFamily: FONT_SANS,
          }}
        >
          {group.name}
        </Box>
        {group.temp && (
          <SchChip
            tone="neutral"
            icon={<ThermostatIcon sx={{ fontSize: 13 }} />}
          >
            {group.temp} °C
          </SchChip>
        )}
        <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>Adherence</Box>
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 16,
            fontWeight: 700,
            color: adhTone,
          }}
        >
          {adh}%
        </Box>
      </Box>
      <Box sx={{ p: '12px 14px 14px' }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: 70, minWidth: 70 }} />
          <Box sx={{ flex: 1 }}>
            <WeekDayHeader />
          </Box>
          <Box sx={{ width: 52 }} />
        </Box>
        {group.members.map((m) => {
          const covPct = Math.round(m.coverage * 100)
          const covTone =
            m.coverage >= 0.85
              ? TOKENS.stateComplete
              : m.coverage >= 0.62
                ? TOKENS.stateRunning
                : TOKENS.ganttDowntime
          return (
            <Box
              key={m.name}
              sx={{ display: 'flex', alignItems: 'center', height: 24 }}
            >
              <Box
                sx={{
                  width: 70,
                  minWidth: 70,
                  textAlign: 'right',
                  pr: '10px',
                  fontSize: 11,
                  fontFamily: FONT_MONO,
                  fontWeight: 600,
                  color: TOKENS.ink700,
                }}
              >
                {m.name}
              </Box>
              <Box sx={{ flex: 1 }}>
                <OWeekTrack height={16}>
                  {group.windows.map((w, i) => (
                    <OBand
                      key={`w${i}`}
                      start={w[0]}
                      end={w[1]}
                      color="transparent"
                      outline={group.color}
                      radius={2}
                      top={0}
                      bottom={0}
                    />
                  ))}
                  {m.runs.map((r, i) => (
                    <OBand
                      key={`r${i}`}
                      start={r.start}
                      end={r.end}
                      color={group.color}
                      radius={2}
                    />
                  ))}
                </OWeekTrack>
              </Box>
              <Box
                sx={{
                  width: 52,
                  textAlign: 'right',
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: covTone,
                }}
              >
                {covPct}%
              </Box>
            </Box>
          )
        })}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mt: '10px',
            pl: '70px',
          }}
        >
          {group.drift.length === 0 ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 11.5,
                color: TOKENS.stateComplete,
                fontWeight: 600,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 15 }} />
              All {group.members.length} lines hold the{' '}
              {group.temp ? `${group.temp} °C` : group.name} block
            </Box>
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 11.5,
                color: TOKENS.ganttDowntime,
                fontWeight: 600,
              }}
            >
              <LinkOffIcon sx={{ fontSize: 15 }} />
              {group.drift.join(', ')} drift
              {group.drift.length === 1 ? 's' : ''} out of sync
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: 10,
              color: TOKENS.ink400,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 9,
                borderRadius: '2px',
                border: `1.5px solid ${group.color}`,
              }}
            />
            required window
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: 10,
              color: TOKENS.ink400,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 9,
                borderRadius: '2px',
                bgcolor: group.color,
              }}
            />
            actual run
          </Box>
        </Box>
      </Box>
    </Box>
  )
})
SyncCard.displayName = 'SyncCard'

const SyncGroupsTab = memo(
  ({
    outputRows,
    syncGroups,
  }: {
    outputRows: InputRow[]
    syncGroups: SynchronizedLinesRowType[]
  }) => {
    const groups = useMemo(
      () => deriveSyncGroups(syncGroups, outputRows),
      [syncGroups, outputRows]
    )
    const drifting = groups.filter((g) => g.drift.length > 0).length
    const avgAdh =
      groups.length > 0
        ? Math.round(
            (groups.reduce((s, g) => s + g.adherence, 0) / groups.length) * 100
          )
        : 0
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<SyncIcon sx={{ fontSize: 19 }} />}
          title="Sync groups"
          sub="Lines that must run a shared temperature block together"
          metrics={[
            { label: 'Groups', value: groups.length },
            {
              label: 'Avg adherence',
              value: avgAdh,
              unit: '%',
              tone: avgAdh >= 85 ? TOKENS.stateComplete : TOKENS.stateRunning,
            },
            {
              label: 'Drifting',
              value: drifting,
              tone: drifting ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            px: '18px',
            py: '16px',
            pb: '24px',
          }}
        >
          {groups.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No temperature sync groups defined.
            </Box>
          ) : (
            groups.map((g) => <SyncCard key={g.id} group={g} />)
          )}
        </Box>
      </Box>
    )
  }
)
SyncGroupsTab.displayName = 'SyncGroupsTab'

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════

interface FactoryScheduleViewProps {
  scheduleId: string
  iteration: string
  /** ABSOLUTE consolidated-horizon rows. The display tabs render `weekRows`
   *  (these rebased by `weekBaseHour`) — only the mid-week reschedule tab
   *  wants them absolute, since `fix_before_hour` and the not-produced keys
   *  are absolute server-side. */
  outputRows: InputRow[]
  selectedWeek: number
  productionRequirements?: ProductionRequirementRowType[]
  syncGroups?: SynchronizedLinesRowType[]
  constraints?: SchedulerResourceAndMaterialConstraintsRowType[]
  // Kitchen throughput rate per (kitchen, base material) — Resource usage
  // tab needs this since kitchen capacity isn't one fixed number, it varies
  // by whatever base material the kitchen is actually running.
  packagingRoutes?: SchedulerPackagingRoutesRowType[]
  // Mid-week reschedule tab — wiring lives one level up (shared with the
  // Gantt's not-produced toggling and the workstation-downtime table).
  downtimeRows?: WorkstationDownTimeRowType[]
  notProducedIds?: Set<string>
  onToggleNotProduced?: (key: string) => void
  // The reschedule tab must wait for a downtime save to land before
  // triggering the run (which reads that same table), so only the awaitable
  // variant is accepted here.
  onSaveDowntimeAsync?: (
    changed: Record<number, WorkstationDownTimeRowType>
  ) => Promise<unknown>
  // Same contract, for the reschedule tab's editable quantities: the run
  // reads the production requirements fresh, so the PUT must land first.
  requirementRows?: ProductionRequirementRowType[]
  onSaveRequirementsAsync?: (
    changed: Record<number, ProductionRequirementRowType>
  ) => Promise<unknown>
  // The reschedule panel's in-progress edits, owned by the page rather than
  // the panel — this component is swapped out for `FactoryBottomDetail`
  // whenever a Factory-layout asset is clicked, so state held below here does
  // not survive an ordinary click. Threaded exactly as `notProducedIds` is,
  // and for the same reason. Nothing here is persisted until the run.
  stillToMake?: Record<string, string>
  setStillToMake?: React.Dispatch<React.SetStateAction<Record<string, string>>>
  dontStartBefore?: Record<string, string>
  setDontStartBefore?: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >
  finishBefore?: Record<string, string>
  setFinishBefore?: React.Dispatch<React.SetStateAction<Record<string, string>>>
  // Absolute-horizon offset for `selectedWeek` from `weekBaseHourFor`:
  // `null` for a single-week schedule, 0 for week 1 of a consolidated run
  // (clipped but not shifted), 168 for week 2, and so on.
  weekBaseHour?: number | null
  // Always the ORIGINAL schedule, regardless of what `outputRows` above is
  // currently showing — every mid-week reschedule is computed independently
  // against the original baseline (deliberate product decision, not a
  // chain), so the checklist must never reflect a prior reschedule's
  // shifted times. Falls back to `outputRows` if not provided.
  midWeekOutputRows?: InputRow[]
  /** A run selected on the timeline, which the Model description filters by. */
  modelFocus?: MDRunFocus | null
  onClearModelFocus?: () => void
  /** Bumped to open the Model description tab (the timeline's "Explain"
   *  action). A counter rather than a flag so the same run can be asked for
   *  twice in a row. */
  revealModelTick?: number
  runMidWeekReschedule?: (params: {
    schedulerId: string
    iteration: string
    config: SchedulerRunConfig
  }) => void
  isMidWeekRunning?: boolean
}

export const FactoryScheduleView = memo(
  ({
    scheduleId,
    iteration,
    outputRows,
    selectedWeek,
    productionRequirements = [],
    syncGroups = [],
    constraints = [],
    packagingRoutes = [],
    downtimeRows = [],
    notProducedIds = new Set<string>(),
    onToggleNotProduced,
    onSaveDowntimeAsync,
    requirementRows = [],
    onSaveRequirementsAsync,
    stillToMake,
    setStillToMake,
    dontStartBefore,
    setDontStartBefore,
    finishBefore,
    setFinishBefore,
    weekBaseHour = null,
    midWeekOutputRows,
    modelFocus = null,
    onClearModelFocus,
    revealModelTick = 0,
    runMidWeekReschedule,
    isMidWeekRunning = false,
  }: FactoryScheduleViewProps) => {
    // Has THIS schedule been published? Any current published week pointing at
    // it counts — the deck gates on "a published schedule", not on the
    // particular week being looked at, and a planner rescheduling week 3 of a
    // plan whose week 2 is published is doing exactly what the feature is for.
    const { data: publishedRows = [] } = usePublishedSchedules()
    const isPublished = useMemo(
      () =>
        publishedRows.some((p) => p.isCurrent && p.schedulerId === scheduleId),
      [publishedRows, scheduleId]
    )

    const [tab, setTab] = useState<TabId>(DEFAULT_TAB_ID)
    useEffect(() => {
      if (revealModelTick > 0) setTab('model')
    }, [revealModelTick])

    const tabButtonSx = (id: TabId) => ({
      border: 'none',
      bgcolor: 'transparent',
      px: '11px',
      py: '8px',
      fontSize: 11.5,
      fontWeight: 600,
      cursor: 'pointer',
      ...T,
      whiteSpace: 'nowrap' as const,
      color: tab === id ? TOKENS.stateComplete : TOKENS.ink400,
      borderBottom:
        tab === id
          ? `2px solid ${TOKENS.stateComplete}`
          : '2px solid transparent',
      mb: '-1px',
    })

    // Every tab below renders against the fixed TOTAL_HOURS grid, so it gets
    // the week-rebased rows. The reschedule tab deliberately does not — see
    // `outputRows`' doc comment.
    const weekRows = useMemo(
      () => rebaseRowsToWeek(outputRows, weekBaseHour),
      [outputRows, weekBaseHour]
    )

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          bgcolor: TOKENS.surface0,
          ...T,
        }}
      >
        {/* Tab strip */}
        <Box
          sx={{
            display: 'flex',
            borderBottom: `1px solid ${TOKENS.line200}`,
            px: '8px',
            flexShrink: 0,
            overflowX: 'auto',
          }}
        >
          {TABS.filter((t) => !isTabHidden(t.id)).map((t) => (
            <Box
              key={t.id}
              component="button"
              onClick={() => setTab(t.id)}
              sx={tabButtonSx(t.id)}
            >
              {t.label}
            </Box>
          ))}
          {/* Pushed right — an action/planning tool, not an output view */}
          <Box sx={{ flex: 1 }} />
          {onToggleNotProduced &&
            onSaveDowntimeAsync &&
            runMidWeekReschedule && (
              <Box
                component="button"
                // Deck slide 7: enabled only on a published schedule, "so that
                // everyone is working on the latest version". Disabled rather
                // than hidden — the tab vanishing would read as the feature
                // being missing, where a greyed tab with a reason reads as a
                // step not yet taken.
                //
                // NOTE this gates the BUTTON only. The deck's fuller intent —
                // that a reschedule is computed FROM the published version —
                // is a separate and much larger piece of work: every
                // reschedule today runs against the ORIGINAL schedule because
                // `lineProductKey` embeds row start times that shift on each
                // reschedule. See ROLES_AND_PUBLISHING_BRIEF.md §4 and D12.
                disabled={!isPublished}
                onClick={() => isPublished && setTab(RESCHEDULE_TAB.id)}
                title={
                  isPublished
                    ? undefined
                    : 'Publish this plan first — a mid-week reschedule must start from the version everyone is working to.'
                }
                sx={{
                  ...tabButtonSx(RESCHEDULE_TAB.id),
                  borderLeft: `1px solid ${TOKENS.line200}`,
                  pl: '14px',
                  ...(isPublished
                    ? {}
                    : { opacity: 0.4, cursor: 'not-allowed' }),
                }}
              >
                {RESCHEDULE_TAB.label}
              </Box>
            )}
        </Box>

        {/* Tab body */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {tab === 'crew' && !isTabHidden('crew') && (
            <CrewCapacityTab outputRows={weekRows} syncGroups={syncGroups} />
          )}
          {tab === 'resources' && (
            <ScheduleResourceUsageView
              scheduleId={scheduleId}
              iteration={iteration}
              selectedWeek={selectedWeek}
              outputRows={weekRows}
              packagingRoutes={packagingRoutes}
              // Declared in the props and passed in by the caller, but never
              // forwarded — so the Resource usage tab saw no capacities and
              // measured every constrained resource against a cap of 0, which
              // is exactly what the capacity labels and the at-limit outlines
              // are read off.
              constraints={constraints}
            />
          )}
          {tab === 'demand' && (
            <DemandFulfilmentTab
              productionRequirements={productionRequirements}
              outputRows={weekRows}
            />
          )}
          {tab === 'model' && (
            <ModelDescriptionTab
              scheduleId={scheduleId}
              iteration={iteration}
              selectedWeek={selectedWeek}
              weekBaseHour={weekBaseHour}
              rows={outputRows}
              focus={modelFocus}
              onClearFocus={onClearModelFocus}
            />
          )}
          {tab === 'sync' && !isTabHidden('sync') && (
            <SyncGroupsTab outputRows={weekRows} syncGroups={syncGroups} />
          )}
          {tab === 'reschedule' &&
            onToggleNotProduced &&
            onSaveDowntimeAsync &&
            runMidWeekReschedule && (
              <MidWeekRescheduleTab
                schedulerId={scheduleId}
                iteration={iteration}
                outputRows={midWeekOutputRows ?? outputRows}
                downtimeRows={downtimeRows}
                notProducedIds={notProducedIds}
                onToggleNotProduced={onToggleNotProduced}
                onSaveDowntimeAsync={onSaveDowntimeAsync}
                requirementRows={requirementRows}
                onSaveRequirementsAsync={onSaveRequirementsAsync}
                stillToMake={stillToMake}
                setStillToMake={setStillToMake}
                dontStartBefore={dontStartBefore}
                setDontStartBefore={setDontStartBefore}
                finishBefore={finishBefore}
                setFinishBefore={setFinishBefore}
                packagingRoutes={packagingRoutes}
                weekBaseHour={weekBaseHour ?? 0}
                runScheduler={runMidWeekReschedule}
                isRunning={isMidWeekRunning}
              />
            )}
        </Box>
      </Box>
    )
  }
)
FactoryScheduleView.displayName = 'FactoryScheduleView'
